<template>
  <div>
    <CRUDList
        ref="crudList"
        :canAdd="false"
        :canEdit="true"
        :canSave="false"
        :showSelect="false"
        :showDate="true"
        :showTotal="true"
        :enableFixedHeaders="true"
        :useSelectedItemsStore="true"
        :displayTopPagination="false"
        :hideDefaultFooter="false"
        :disablePagination="false"
        :loadingData="loading"
        :headers="headers"
        :baseCrudKey="baseCrudKey"
        :dialogHeaderName="dialogHeaderName"
        :defaultSortBy="defaultSortBy"
        :footerTotalDisplayColumnWise="footerTotals()"
        tableWrapperDynamicClassname='elevation-3 ma-3 pa-1 taxroll-table-wrapper'
        tableDynamicClassname='taxroll-custom-table'
        tableDynamicHeight='200'
        :variableToChangeRowBgColor="[{ varName: 'itsNewRecord', varCol: '#add8e6' }]"
        @loadCRUDList="handleLoadCRUDList"
        @emitRowButtonClick="addUnpaidRowsForRow"
        @emitInlineSelectChange="handleRowFieldChange"
        @emitRowIconClick="handleRowIconClick"
        @onSingleClicked="handleRowClicked"
      >
      <!-- :variableToChangeRowBgColor="[{ varName: 'itsNewRecord', varCol: '#add8e6' }]"
           is set above so a newly added record is shaded and the user can identify it -->
          <template v-slot:[`editedItem`]="{}">
            <TaxEditDialog
              :baseCrudKey="baseCrudKey"
              :parentCrudKey="parentCrudKey"
              :halfPaymentFlag="halfPaymentFlag"
              @dataChanged="handleTaxDetailChanged"
              @delete="handleTaxDetailDeleted" />
          </template>

          <!-- The footer row dialogTaxDetails carried under its grid: the parcel's mortgage code,
               the "Other Taxes Due" flag, Add Comment and the running total. Ported here because
               that component is being retired - see scanForUnpaidTaxes for the unpaid scan behind
               the flag. One of its buttons has since moved out of this row: Add All Unpaids is a
               cell in each grid row, right after Owner Name, on the rows whose parcel still has
               unpaid records (buildHeaders / rowUnpaidRecords / addUnpaidRowsForRow).
               Mtg Code and the flag answer for the row the clerk clicked - see selectedRow. -->
          <template v-slot:addidtionalCrudFooter>
            <!-- Every item sizes to its own text and is spaced apart rather than dropped into
                 twelfths: at fixed widths "Other Taxes Due" and "Running Total: 22,862.18" each
                 wrapped onto a second line while the columns either side of them sat empty.
                 The rule that used to be drawn per label (a broken line with gaps where a column
                 had nothing in it) is drawn once, on the row. -->
            <v-row
              class="ma-0 large-payment-footer-row"
              align="center"
              no-gutters
              style="width: 100%;">
              <v-col cols="auto" class="pb-1">
                <div class="blue-text-left">Mtg Code: {{ mtgCodeDisplay }}</div>
              </v-col>
              <v-col cols="auto" class="pb-1">
                <div
                  v-if="hasOtherTaxesDue"
                  class="blue-text-left large-payment-other-taxes-due"
                  style="color: red !important;">Other Taxes Due</div>
              </v-col>
              <v-spacer />
              <!-- Add Comment: the comment is the grid's - one comment written onto every tax
                   record on the payment - so the button belongs under the list, centred in this
                   row between Mtg Code and the Running Total. It spent a while up beside the
                   Parcel ID lookup in paymentDialog, which is the payment's own header.
                   Not the glow-on-for-add-all-unpaids treatment it carried there: that class is
                   white text on a near-white fill, which left the label all but unreadable.
                   Vuetify 2 has no tonal variant - outlined is the readable equivalent. -->
              <v-col cols="auto" class="pb-1">
                <v-btn
                  class="large-payment-add-comment"
                  id="largePaymentAddComment"
                  outlined
                  color="primary"
                  small
                  @click="addComments">Add Comment</v-btn>
              </v-col>
              <v-spacer />
              <!-- Running Total sat at the right-hand end of this row, where it landed on top of
                   the grid's own footer: the slot is pulled up over it, so the page count and
                   arrows print through the total. The row stops short of the pager, so the end of
                   the row is as far right as this can go. -->
              <v-col cols="auto" class="pb-1">
                <div class="blue-text large-payment-running-total">
                  Running Total: {{ runningTotal }}</div>
              </v-col>
              <!-- Add All Unpaids moved into the grid as a per-row button (see the note above).
                <v-col cols="5" class="pb-1">
                <v-btn class="mt-2 large-payment-add-all-unpaids"
                  id="largePaymentAddAllUnpaids"
                  v-if="hasOtherTaxesDue" color="error" small
                  :loading="taxRowLookupLoading"
                  @click="addAllUnpaidRows">Add All Unpaids</v-btn>
                </v-col>
                -->
            </v-row>
          </template>

    </CRUDList>

    <!-- Add Tax Details Section - For adding new items only. Commented out along with
         everything that only served it: the dialogTaxDetails import / registration,
         newTaxDetailsToAdd, addTaxCrudKey, hasValidItemsToAdd, commitStagedTaxRows,
         addToList, recalculatePenaltiesForPicklist and the newTaxDetailsToAdd watcher.
         Rows are added by the payment dialog header's Tax Yr / Tax ID and Parcel ID lookups
         now - see addTaxRowByYearAndId / addUnpaidRowsByParcelId.
         The inner "Add to List" comment is folded into this one - HTML comments cannot nest.

    <v-card class="ma-1">
      <div class="text-subtitle-1 pa-1 pb-0">
        <div class="d-flex justify-space-between align-center" style="width: 100%;">
          <div>
            <v-icon left small>mdi-plus-circle</v-icon>
            Add Tax Details to Payment
          </div>
          [was already commented] Add to List is now committed automatically - by the Payment
               Info fields (paymentFieldActivity) and once an unpaids / parcel load finishes
               (unpaidTaxesAdded) - so the clerk no longer has to click it.
          <v-btn
            x-small
            color="primary"
            @click="addToList"
            :disabled="!hasValidItemsToAdd"
            class="mr-2"
          >
            <v-icon left x-small>mdi-check</v-icon>
            Add to List
          </v-btn>
          [end already-commented block]

        </div>
      </div>
      <div class="pa-0 pt-0">
        <dialogTaxDetails
          ref="dialogTaxDetailsRef"
          v-model="newTaxDetailsToAdd"
          :baseCrudKey="addTaxCrudKey"
          :fiscalYear="editedItem.fiscalYear"
          :loadMoreTaxBtn="false"
          :isItFromLargePayments="true"
          @unpaidTaxesAdded="commitStagedTaxRows"
          @commitStagedTaxRows="commitStagedTaxRows" />
      </div>
    </v-card>
    -->
    <v-dialog v-model="commentDialog" max-width="600" class="input-text-size">
      <v-card>
        <v-card-title class="input-text-size">Add Comments</v-card-title>
        <v-card-text class="input-text-size">
          <v-container>
            <v-row>
              <v-col cols="6" class="mt-n3">
                  <CommonDropDowns v-if="commentDialog"
                   :isItCategory=true @change="dataFromCommonDropDowns"
          :commentsCategoryValue="commentData.category" :isCommentsCategoryDisabled="true" />
              </v-col>
               <v-col cols="6" class="pt-2 pb-0 mb-n4 mt-n2">
        <v-autocomplete :items="reasonListDropDown" class="input-text-size" label='Reason Code'
         v-model="commentData.reasonCode" item-text="name" item-value="value" />
      </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-checkbox
                  v-model="commentData.addToLegal"
                  label="Add To Legal on Statement"
                  dense
                  hide-details
                  class="input-text-size"
                />
              </v-col>
              <v-col cols="4">
                <v-checkbox
                  v-model="commentData.sendToWebsite"
                  label="Send To Website"
                  dense
                  hide-details
                  class="input-text-size"
                />
              </v-col>
              <v-col cols="4">
                <v-checkbox
                  v-model="commentData.rollToNextYear"
                  label="Roll To Next Year"
                  dense
                  hide-details
                  class="input-text-size"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="commentData.comment"
                  label="Comments"
                  rows="4"
                  dense
                  outlined
                  hide-details
                  class="input-text-size"
                  placeholder="Enter your comment here..."
                  v-textAreaUppercase=globalSettingItem.conEverythingToCaps
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="input-text-size">
          <v-spacer></v-spacer>
          <v-btn      :class="`input-text-size`"
            text color="blue darken-1"
             small @click="closeCommentDialog">Cancel</v-btn>
          <v-btn   :class="`input-text-size`"
            text color="blue darken-1" small @click="handleBulkCommentSave">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- section for show warning message -->
     <CommonPopUp v-model="showOrHideWarningMessage" v-if="showOrHideWarningMessage"
        :enableFlashTypeMsg="true" flashType="warning" focus-default-btn
        @onEnter="openConfirmPopUp('Continue')">
        <template v-slot:customsLabelOrInfo>
          <!-- Load-time warnings arrive as plain strings; the ones a Tax Yr / Tax ID or Parcel
               ID lookup raises arrive as { txtName, txtMsg } so each line can be coloured the
               way the payment screen colours it - red for the two that mean the money may
               already be collected. Both shapes render here. -->
          <div v-if="warningMessage && warningMessage !==undefined">
            <div v-for="(message, index) in warningMessage" :key="index"
             class="mt-3 d-flex align-start">
               <p style="white-space: pre;">
            <v-icon :class="handleCircleClass(message.txtName || 'statusCodePayment')">
              {{ icons.mdiCircleSlice8 }}
            </v-icon> {{ message.txtMsg || message }}
          </p>
            </div>
          </div>
        </template>
        <template v-slot:[`customsButtonsForPopUp`]="{}">
          <!-- Continue is the default action: CommonPopUp focuses it once the dialog has
               opened, so the clerk can clear the warning with Enter alone - no Tab first. -->
          <v-btn class="input-text-size payment-transaction-list-continue-btn"
          id="paymentTransactionListContinueBtn"
          color="primary" data-default-btn
          @click="openConfirmPopUp('Continue')">Continue</v-btn>
          <v-btn class="input-text-size payment-transaction-list-cancel-btn"
          id="paymentTransactionListCancelBtn"
          text color="blue darken-1"
          @click="openConfirmPopUp('Cancel')">Cancel</v-btn>
        </template>
      </CommonPopUp>
  </div>
</template>

<script>
import {
  mdiCircleSlice8,
} from '@mdi/js';
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import CRUDList from '../../../../components/common/base/crud-2.0/CRUDList.vue';
import largePaymentHeaders from '../../../../components/tax/TaxRollAdjustments/TaxRollAdjustmentsUtils/paymentHeaders.util';
import CommonPopUp from '../../../../components/tax/common-utils/CommonPopUp.vue';
// getFeeListBasedOnEffeciveDate, exemptManufacturingStatusCode, penaltyCalculation and toShortDate
// were only reached from recalculatePenaltiesForPicklist, commented out below. amountFormat is
// back - the footer's Running Total formats through it:
// import { getFeeListBasedOnEffeciveDate, taxReasonListDropDownPostProcessTrans }
//   from '../../../../components/tax/common-utils/CommonTaxFunctionalities';
// import { exemptManufacturingStatusCode, penaltyCalculation }
//   from '../../../../components/tax/common-utils/tax-common.util';
// import { toShortDate } from '../../../../util/shared/tmc-global';
import { getPreviousPenaltyPeriodEnd, taxReasonListDropDownPostProcessTrans } from '../../../../components/tax/common-utils/CommonTaxFunctionalities';
import PaymentCommonFuncMixin from '../../../../components/tax/common-utils/PaymentCommonFunc.mixin';
import {
  buildTaxPaymentTypeOptions,
  DEFAULT_TAX_PAYMENT_TYPE,
  isProtestRefundOrCredit,
  partitionPendingPaymentAlertCodes,
} from '../../../../components/tax/common-utils/tax-common.util';
import { applyManualChangesToRow } from '../../../../components/tax/payments/audit/paymentAudit.util';
// only the commented-out "Add Tax Details to Payment" card mounted this
// import dialogTaxDetails
//   from '../../../../components/tax/payments/paymenteditDialog/dialogTaxDetails.vue';
import { toIsoDate } from '../../../../util/shared/tmc-global';
import { amountFormat, amountToNumber } from '../../../../util/shared/vue-global';
// shared with TaxEditDialog - one home for the penalty / fee / half-pay math
import {
  buildTaxLookupCriteria, buildTaxRowFromInquiry, collectTaxRowWarnings,
  hasPendingPaymentAlertCode, isNewTaxRowId, isSameRecordAndType, newTaxRowId, normalizeParcelId,
} from './taxRowLookup.util';
import TaxEditDialog from './TaxEditDialog.vue';
import CommonDropDowns from '../../../../components/tax/common-utils/CommonDropDowns.mixin.vue';
import GlobalToCaps from '../../../../util/shared/GlobalToCaps.mixin.vue';

export default {
  name: 'LargePaymentList',
  mixins: [PaymentCommonFuncMixin, GlobalToCaps],

  components: {
    CRUDList,
    TaxEditDialog,
    // dialogTaxDetails,
    CommonPopUp,
    CommonDropDowns,
  },
  props: {
    fiscalYear: {
      type: [String, Number],
      required: false,
      default: null,
    },
    paymentNumber: {
      type: [String, Number],
      required: false,
      default: null,
    },
    taxDetails: {
      type: Array,
      required: false,
      default: () => [],
    },
    totalDue: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    totalPaid: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    cashChange: {
      type: [String, Number],
      required: false,
      default: 0,
    },
    /**
     * The crud key the payment this grid belongs to lives under. It used to be hardcoded to
     * 'pickListMakePaymentDialog' everywhere, which is what stopped this grid from standing in
     * for dialogTaxDetails on the payment screen - every write landed on the picklist's item
     * instead of the payment being entered. The default keeps the picklist / existing-payment
     * branches on exactly the key they have always used.
     */
    parentCrudKey: {
      type: String,
      required: false,
      default: 'pickListMakePaymentDialog',
    },
    /**
     * A payment being entered that has no number yet. Its rows exist only in this grid until it
     * is saved, so there is nothing to load and no criteria to load it by - see
     * handleLoadCRUDList and resetForNewPayment.
     */
    isNewPayment: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      baseCrudKey: 'largePaymentList',
      // all three belonged to the commented-out "Add Tax Details to Payment" card
      // addTaxCrudKey: 'largePaymentListAddNew', // Separate crud key for adding items
      dialogHeaderName: 'Payment Transactions',
      headers: this.buildHeaders(),
      editDialog: false,
      editedIndex: -1,
      // newTaxDetailsToAdd: [], // Separate array for new items being added
      // addTaxPanelExpanded: undefined, // Controls expansion panel state (undefined = collapsed)
      halfPaymentFlag: false,
      // in-flight guard for the header's Tax Yr / Tax ID lookup
      taxRowLookupLoading: false,
      // Rows a lookup resolved that raised a warning: held here, not in the grid, until the
      // clerk answers the popup. Continue commits them, Cancel drops them.
      pendingTaxRows: [],
      loading: false,
      showOrHideWarningMessage: false,
      warningMessage: [],
      mtgCodeErrorList: [],
      icons: {
        mdiCircleSlice8,
      },
      textName: {
        txtName: 'defaultText', // You can change this based on your logic
      },
      // API-side totals (from largePaymentRecords API - not affected by pagination)
      serverTotals: {
        totalTaxAmt: 0,
        totalDue: 0,
        totalPenalty: 0,
        totalFees: 0,
        totalRecords: 0,
      },
      commentData: {
        category: 7, // Default category for user comments
        reasonCode: null,
        addToLegal: false,
        sendToWebsite: false,
        rollToNextYear: false,
        comment: '',
      },
      commentDialog: false,
      /**
       * Unpaid records found for the parcels that have been asked about, keyed by baseId. Cached
       * so that adding or removing a row only re-filters what is already known (unpaidTaxRecords)
       * instead of re-querying - a parcel is asked about once.
       */
      unpaidScanCache: {},
      /**
       * The parcels a scan is in flight for, so two records arriving in the same breath cannot
       * both query the same one. A single in-flight boolean would not do: it made the second
       * arrival's parcel go unasked-about rather than queued behind the first.
       */
      unpaidScanInFlight: [],
      /**
       * The row the clerk last clicked. The footer's Mtg Code and "Other Taxes Due" answer for it
       * rather than for the payment as a whole - see selectedRow.
       */
      clickedRow: null,
      // The records 'Load Unpaids Automatically' has already taken, so each is tried once - see
      // autoLoadUnpaidRows.
      autoLoadedRecordIds: [],
      /**
       * The pick list has been queried for this dialog session. It is queried ONCE: the backend
       * ignores skip/limit and hands back the whole selection (pickListLargePaymentsHandler), so
       * every row is already here and the grid pages and sorts them itself - see
       * gridServerItemsLength.
       * Re-querying on a page or sort change is what lost a clerk's work: the response overwrote
       * both the grid rows and the payment's taxDetails, so the amounts keyed on the page being
       * left went back to what the database said. McIntosh payment 657 - 116 prepaid mobile
       * homes, ~30 amounts edited on page 1, all of them gone on the click through to page 2.
       * Cleared in resetStoreData, so the next payment loads its own pick list.
       */
      pickListLoaded: false,
    };
  },
  computed: {
    ...mapGetters('base/crud', [
      'criteria', 'items', 'item', 'isVisible',
    ]),
    ...mapGetters('user', ['getSettings']),
    ...mapState({
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem || {},
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem || {},
      reasonListDropDown: (state) => state.base.crud.items.reasonListDropDown,
      // the Type cell's options come off the same enum every other Type dropdown reads
      statusTypes: (state) => state.enums.TaxPaymentEnum || [],
      skipNextApiCallForLargePayments: (state) => state
        .tax.taxPayment.skipNextApiCallForLargePayments || false,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
    }),
    /**
     * The Type cell's options - 1 Tax Payment ... 7 Other, numbered by the enum's own order,
     * the same list the payment dialog's Add Tax Records band and TaxEditDialog offer.
     * A computed, and watched into the headers below, because App.vue fires cacheEnums()
     * without awaiting it: this grid can render before TaxPaymentEnum has arrived.
     */
    taxPaymentTypeOptions() {
      return buildTaxPaymentTypeOptions(this.statusTypes);
    },
    defaultSortBy() {
      return [
        {
          column: 'taxYear',
          direction: 'desc',
        },
        {
          column: 'taxId',
          direction: 'desc',
        },

      ];
    },
    criteriaState: {
      get() {
        return this.criteria(this.baseCrudKey);
      },
      set(value) {
        this.setCriteria([this.baseCrudKey, value]);
      },
    },
    // the payment these rows belong to - the picklist's on the picklist branch, the one being
    // entered on the payment screen (see the parentCrudKey prop)
    editedItem: {
      get() {
        return this.item(this.parentCrudKey);
      },
      set(value) {
        this.setItem([this.parentCrudKey, value]);
      },
    },
    // Whether the dialog holding this grid is open. A new payment reuses the same dialog - and
    // so this same component - so this is what tells the grid a fresh payment has started.
    parentDialogIsOpen() {
      return this.isVisible(this.parentCrudKey);
    },
    /**
     * Whether every row of this payment is already in the browser, which is what lets the grid
     * page and sort them itself. Two cases: a pick-list selection, which is queried once per
     * dialog session (pickListLoaded) and comes back whole, and a payment being entered, whose
     * rows exist nowhere but this grid until it is saved (isNewPayment).
     * The opposite - a payment that has been saved and has a number - really is fetched one page
     * at a time by loadLargePaymentData, and is left server-paged.
     */
    isClientPagedPayment() {
      return !(this.fiscalYear && this.paymentNumber);
    },
    skipNextApiCallForLargePaymentsFlag: {
      // Flag to prevent unnecessary API calls for large payments
      get() {
        return this.skipNextApiCallForLargePayments;
      },
      set(value) {
        this.setSkipNextApiCallForLargePayments(value);
      },
    },
    // Calculate totals - use API totals for pagination, fallback to client-side
    calculatedTotals() {
      // If we have server-side totals from API, use them (accurate across all pages)
      if (this.serverTotals && this.serverTotals.totalRecords > 0) {
        return {
          taxAmt: amountToNumber(this.serverTotals.totalTaxAmt),
          penaltyAmt: amountToNumber(this.serverTotals.totalPenalty),
          totalFees: amountToNumber(this.serverTotals.totalFees),
          total: amountToNumber(this.serverTotals.totalDue),
          totalRecords: this.serverTotals.totalRecords || 0,
        };
      }

      // Fallback: Calculate from current page data (for picklist mode)
      const items = this.items(this.baseCrudKey) || [];
      // Include items that are TAXPAYMENT or don't have taxType set (new items)
      const taxItemsWithTaxPaymentTaxType = (items || [])
        .filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND') || [];
      return {
        taxAmt: taxItemsWithTaxPaymentTaxType
          .reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
        penaltyAmt: taxItemsWithTaxPaymentTaxType
          .reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
        totalFees: taxItemsWithTaxPaymentTaxType
          .reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
        total: taxItemsWithTaxPaymentTaxType
          .reduce((sum, item) => sum + amountToNumber(item.total), 0),
        totalRecords: items.length,
      };
    },
    /**
     * The footer's Running Total - the grid's grand total (tax + penalty + fees), which is what
     * dialogTaxDetails showed there. Reads the same calculatedTotals the footer row does, so it
     * stays right across pagination on a server-paged payment.
     */
    runningTotal() {
      return amountFormat(this.calculatedTotals.total);
    },
    /**
     * The page controls under the grid: the ones every other grid in the app gets - a page number
     * between a back and a next arrow (store/user/private.js footerProps). This grid used to
     * build its own instead, which added the "1-2 of 2" page text and the first / last jump
     * arrows; the page text also printed through the Mtg Code / Running Total row above it.
     * Page SIZE is not set here - it stays on each pagination item's itemsperpage, which the
     * server-paged branches take off the criteria's limit.
     */
    gridFooterProps() {
      return this.getSettings.footerProps;
    },
    /**
     * The row the footer answers for: the one the clerk clicked, as long as it is still on the
     * payment. dialogTaxDetails' footer described the single record it had looked up; the rows
     * here can span parcels, so clicking a record is what says which one "Mtg Code" and "Other
     * Taxes Due" are about. With nothing clicked - a payment just opened - they describe the
     * whole payment, as they did before.
     * Matched against the live rows so a deleted or reloaded row falls back rather than leaving
     * the footer describing a record that is no longer there.
     */
    selectedRow() {
      const clicked = this.clickedRow;
      if (!clicked) return null;
      const rows = this.items(this.baseCrudKey) || [];
      const isTheClickedRow = (row) => (clicked._id
        ? row._id === clicked._id
        : (Number(row.taxYear) === Number(clicked.taxYear)
          && Number(row.taxId) === Number(clicked.taxId)));
      return rows.some(isTheClickedRow) ? clicked : null;
    },
    /**
     * The clicked row's mortgage code. With no row clicked every distinct code on the payment is
     * listed rather than picking one of them arbitrarily.
     */
    mtgCodeDisplay() {
      if (this.selectedRow) {
        const code = this.selectedRow.mtgCode;
        return (code === null || code === undefined) ? '' : String(code).trim();
      }
      const codes = (this.items(this.baseCrudKey) || [])
        .map((row) => (row.mtgCode === null || row.mtgCode === undefined
          ? '' : String(row.mtgCode).trim()))
        .filter((code) => code);
      return [...new Set(codes)].join(', ');
    },
    // Distinct baseIds of the rows on the payment - the parcels the unpaid scan asks about.
    paymentBaseIds() {
      const baseIds = (this.items(this.baseCrudKey) || [])
        .map((row) => row.baseId)
        .filter((baseId) => baseId !== null && baseId !== undefined && baseId !== '');
      return [...new Set(baseIds.map((baseId) => Number(baseId)))].sort((a, b) => a - b);
    },
    /**
     * The scanned parcels' unpaid records that are not on this payment yet - what Add All Unpaids
     * would load. Filtered against the grid AND against pendingTaxRows, since a row held behind an
     * unanswered warning popup is already spoken for and must not be offered a second time.
     */
    unpaidTaxRecords() {
      const rows = [...(this.items(this.baseCrudKey) || []), ...this.pendingTaxRows];
      const alreadyOnPayment = (rec) => rows.some((row) => (
        (row.taxInquiryId && row.taxInquiryId === rec._id)
        || (Number(row.taxYear) === Number(rec.taxYear)
          && Number(row.taxId) === Number(rec.taxId))));
      const scanned = this.paymentBaseIds
        .reduce((recs, baseId) => recs.concat(this.unpaidScanCache[baseId] || []), []);
      // one parcel can be reached through several of its records - dedupe before filtering
      const byId = new Map(scanned.map((rec) => [rec._id, rec]));
      return [...byId.values()].filter((rec) => !alreadyOnPayment(rec));
    },
    /**
     * 'Load Unpaids Automatically' (payment settings). On, the records the scan finds go onto the
     * payment by themselves and no row offers an Add Unpaids button - the clerk never asks,
     * because it is already done. Off, every load is a click on that button.
     * This is the setting dialogTaxDetails read as isAutomaticAddOn in commonLoadTaxInquiryFunc,
     * where it pulled the parcel's unpaids after a keyed record resolved.
     */
    autoLoadUnpaids() {
      return (this.paymentSettingItem || {}).loadUnpaidsAutomatically || false;
    },
    /**
     * The flag: taxes still due on the clicked row's parcel, or - with no row clicked - anywhere
     * on the payment. It is the same set the row's own Add Unpaids button loads, so a row showing
     * the button and the flag showing for that row always agree. With the setting on it is what
     * is left over - a parcel whose records were held behind a cancelled warning popup.
     */
    hasOtherTaxesDue() {
      if (this.selectedRow) return this.rowHasUnpaids(this.selectedRow);
      return this.unpaidTaxRecords.length > 0;
    },
    /* Gated the commented-out ADD TO LIST button and the staged-row commits.
    // Check if there are valid items ready to be added
    hasValidItemsToAdd() {
      if (!this.newTaxDetailsToAdd || this.newTaxDetailsToAdd.length === 0) {
        return false;
      }
      // Enable button when there are items that have been fetched (have ownerName populated)
      return this.newTaxDetailsToAdd.some((item) => item.taxYear
        && item.taxId
        && item.ownerName);
    },
    */
  },
  methods: {
    buildHeaders(showCert) {
      const useCert = showCert !== undefined
        ? showCert
        : ((this.paymentSettingItem || {}).showCertNumberOnPaymentScreen || false);
      const headers = largePaymentHeaders.map((h) => {
        if (h.value === 'reciept') {
          return useCert
            ? { ...h, text: 'Cert No', value: 'certNumber' }
            : h;
        }
        // The Type cell is a dropdown on THIS grid - a refund or a cancelled receipt can be
        // retyped on the line rather than through TaxEditDialog (see handleRowFieldChange).
        // Overridden here rather than in paymentHeaders.util because that list is shared with
        // the payment transaction list and TaxEditDialog, which only display the type.
        if (h.value === 'taxType') {
          return {
            ...h,
            formatter: 'inlineSelect',
            selectItems: this.taxPaymentTypeOptions,
            // the same gate TaxEditDialog's isRowLocked applies to its own Type dropdown: a
            // posted or voided line is not the clerk's to retype
            lockEditIf: (row) => !!(row.posted || row.void || row.tempVariableVoid),
            // anything other than 1 Tax Payment stands out in a column of 1s
            highlightIf: (row) => !!row.taxType && row.taxType !== DEFAULT_TAX_PAYMENT_TYPE,
            selectWidth: '62px',
          };
        }
        return h;
      });
      // The Add All Unpaids cell, right after Owner Name - the footer button moved into the grid.
      // It carries no header text and no field of its own: CRUDRow renders the button only where
      // showButtonIf says so, so a row whose parcel has nothing else due shows an empty cell.
      // Added here rather than in paymentHeaders.util because that list is shared with the
      // payment transaction list and TaxEditDialog, which have no unpaid scan behind them.
      const unpaidsHeader = {
        // `text`, not Vuetify 3's `title` - the rest of this list is keyed the same way
        text: '',
        value: 'addAllUnpaids',
        formatter: 'button',
        buttonText: 'Add Unpaids',
        // red, the way the footer button was - it is a prompt that money is still owed on the
        // parcel, and has to read as one from inside a row of figures
        buttonColor: 'error',
        showButtonIf: (row) => this.rowHasUnpaids(row),
        sortable: false,
      };
      const ownerIndex = headers.findIndex((h) => h.value === 'ownerName');
      if (ownerIndex === -1) headers.push(unpaidsHeader);
      else headers.splice(ownerIndex + 1, 0, unpaidsHeader);
      // The trash can, first in the row so it is on screen without scrolling right - what the old
      // dialogTaxDetails grid had on every row and this one lost. Takes a row off the payment
      // without opening it (handleRowIconClick); the same enable rule as the one in TaxEditDialog.
      // Added here rather than in paymentHeaders.util for the same reason as the unpaids cell.
      headers.unshift({
        text: '',
        value: 'removeRow',
        formatter: 'iconButton',
        icon: 'mdiDelete',
        tooltip: 'Remove from this payment',
        disableIf: (row) => !this.isTaxRowRemovable(row),
        disabledTooltip: (row) => (row.void
          ? 'Already voided - open the record to review it'
          : 'Already saved - open the record and Void it'),
        sortable: false,
        width: '1%',
      });
      // A voided row is otherwise only distinguishable by its zeroed amounts. dialogTaxDetails
      // has always carried the flag, so the list shows it too - appended here rather than in
      // the shared header list, which the standalone large-payments view also uses.
      return [...headers, {
        text: 'Void',
        value: 'void',
        align: 'center',
        sortable: false,
        formatter: 'flagToYorEmpty',
      }];
    },
    ...mapActions('tax/taxPayment', [
      'loadLargePayments',
      'pickListLargePayments',
    ]),
    ...mapMutations('base/crud', [
      'setCriteria',
      'setItem',
      'setItems',
      'setPaginationItems',
    ]),
    ...mapMutations('tax/taxPayment', [
      'setHalfOrPostmark',
      'setPaymentShotCutStore',
      'setSkipNextApiCallForLargePayments',
    ]),
    ...mapActions('shared/taxReasonCode', [
      'loadReasonCode',
    ]),
    // the header Tax Yr / Tax ID lookup resolves the record and prices its fees
    ...mapActions('tax/taxInquires', ['getTaxInquiryForPayment']),
    ...mapActions('tax/taxFee', ['loadTaxRollFeesTable']),
    // ...and warns on what the payment screen warns on before the money is taken
    ...mapActions('tax/taxPayment', ['loadPayment', 'getPendingPaymentDetails']),
    ...mapActions('tax/taxComments', [
      'bulkUpsertComments',
    ]),
    ...mapActions(['flashWarn']),
    /* ------------------------------------------------------------------------------------
     * ONE place rows are written on this screen.
     *
     * This screen keeps the payment's rows in TWO stores - the payment record's
     * editedItem.taxDetails (crud key 'pickListMakePaymentDialog') and the grid's own items
     * (crud key 'largePaymentList') - and every write path used to update them with its own
     * setItem/setItems/emit combination. When two of those paths ran for the same row (the
     * newTaxDetailsToAdd watcher and addToList both commit a staged row, and only the watcher
     * de-duplicated) the save payload ended up carrying a copy from each store.
     *
     * That is Pontotoc payment 719: one create payload with 2023/9804 on it twice, receipts
     * 29848 and 29849, 875.15 each, against a single 875.15 card. It was out of balance from
     * creation and the cleanup was manual.
     *
     * So: commitTaxRows is the only writer. It de-duplicates, writes both stores from the same
     * array, emits once, and recomputes the footer. Everything that changes rows - the staged
     * "Add Tax Details" commit, the unpaids/parcel auto-load, a row edit or void from
     * TaxEditDialog, a row delete - goes through it.
     * ---------------------------------------------------------------------------------- */

    /**
     * What makes two rows the same line. Deliberately not taxYear/taxId alone: the same parcel
     * may legitimately appear twice on one payment as a payment line and a refund line, or as a
     * protested and an unprotested portion. Mirrors the server's own guard
     * (findDuplicateLiveTaxDetail in taxPayments.util.js) so the two agree on what a duplicate is.
     *
     * Returns '' for a row that is not yet resolved to a parcel - a half-typed row is not a
     * duplicate of anything.
     */
    taxRowIdentity(row) {
      const taxYear = amountToNumber(row.taxYear);
      // A row keyed by parcel ID carries the typed PID in taxId and the resolved tax ID in
      // originalTaxId; upsertNOverPaymentsCommon sends the resolved one, so match on that.
      const taxId = amountToNumber(row.originalTaxId) || amountToNumber(row.taxId);
      if (!taxYear || !taxId) return '';
      return [taxYear, taxId, row.taxType || 'TAXPAYMENT', row.protest ? 1 : 0].join('|');
    },
    // A row already written to paymenttaxdetails outranks an unsaved copy of the same line:
    // dropping the saved one would orphan the stored row instead of correcting the duplicate.
    taxRowRank(row) {
      if (row._id) return 2;
      if (row.reciept) return 1;
      return 0;
    },
    /**
     * Collapses duplicate LIVE rows, keeping the saved copy where there is one.
     *
     * Void and posted rows are passed through untouched and never participate: voiding a row is
     * how a clerk corrects a duplicate, and a parcel whose earlier line was voided may genuinely
     * be added again.
     */
    dedupeTaxRows(rows) {
      const kept = [];
      const keptIndexByKey = new Map();
      const dropped = [];
      (rows || []).forEach((row) => {
        if (!row) return;
        const isLive = !row.void && !row.tempVariableVoid && !row.posted;
        const key = isLive ? this.taxRowIdentity(row) : '';
        if (!key) {
          kept.push(row);
          return;
        }
        const existingIndex = keptIndexByKey.get(key);
        if (existingIndex === undefined) {
          keptIndexByKey.set(key, kept.push(row) - 1);
          return;
        }
        const existing = kept[existingIndex];
        if (this.taxRowRank(row) > this.taxRowRank(existing)) {
          kept[existingIndex] = row;
          dropped.push(existing);
        } else {
          dropped.push(row);
        }
      });
      return { rows: kept, dropped };
    },
    /**
     * Says "the rows just changed here, locally - do not go and fetch them again", by holding
     * handleLoadCRUDList off for half a second.
     *
     * Only armed on a server-paged payment, which is the only kind that still re-fetches. It was
     * the sole protection a pick-list payment had, and half a second is no protection at all: a
     * clerk who edits a row and then pages - which is the whole point of a 116-record payment -
     * is well past the timer, and the reload took the edits with it (McIntosh payment 657).
     * Those payments hold every row in the browser now and never re-query, so there is nothing
     * left for this to guard.
     */
    guardLocalRowWrite() {
      if (this.isClientPagedPayment) return;
      this.skipNextApiCallForLargePaymentsFlag = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.skipNextApiCallForLargePaymentsFlag = false;
        }, 500);
      });
    },
    /**
     * What goes into the grid's pagination as `serverItemsLength`.
     *
     * -1 is v-data-table's "these are all the rows there are": it then filters, sorts and pages
     * the array it has been given instead of asking for the next page. That is exactly right for
     * a pick list or a payment being entered - every row is already here - and it is what makes
     * paging and sorting free of a round trip, so no reload can overwrite what the clerk keyed.
     * A saved payment keeps the real record count: only one page of it is ever in the grid, so
     * the footer has to be told how many rows the payment actually has.
     */
    gridServerItemsLength() {
      return this.isClientPagedPayment ? -1 : this.serverTotals.totalRecords;
    },
    /**
     * Writes `nextRows` as THE rows of this payment: the grid and the payment record both get
     * this exact array, the parent is told once, and the footer totals are recomputed.
     *
     * addedItems / deletedItems only feed the delta-based footer maths
     * (updateLocalTotalsAndPagination); the rows themselves always come from nextRows.
     */
    commitTaxRows(nextRows, { addedItems = [], deletedItems = [], warnOnDuplicate = true } = {}) {
      const { rows, dropped } = this.dedupeTaxRows(nextRows);
      if (warnOnDuplicate) {
        dropped.forEach((row) => this.flashWarn(
          `Tax Yr ${row.taxYear} Tax ID ${row.originalTaxId || row.taxId} has already been added to this payment.`,
        ));
      }

      // Purely local: nothing here needs re-fetching, and a reload would undo the write.
      this.guardLocalRowWrite();

      this.setItems([this.baseCrudKey, rows]);
      // NOT setItem([baseCrudKey, ...]) as addToList used to do: that slot holds the single row
      // TaxEditDialog is editing, and writing the whole payment into it replaces the row under
      // an open dialog. The grid's rows live in items(baseCrudKey); the payment lives in
      // item(parentCrudKey).
      this.setItem([this.parentCrudKey, { ...(this.editedItem || {}), taxDetails: rows }]);
      this.$emit('taxDetailsChanged', rows);

      const keptAdded = (addedItems || []).filter((item) => !dropped.includes(item));
      this.updateLocalTotalsAndPagination(rows, keptAdded, deletedItems);

      return { rows, dropped };
    },
    /**
     * Whether `row` is the row `target` describes. Identity first (_id, then receipt), and only
     * then the parcel pair - two live lines for one parcel would otherwise both answer yes, which
     * is how voiding ONE of a duplicated pair in TaxEditDialog could land on the wrong row or on
     * both of them.
     */
    isSameTaxRow(row, target) {
      if (row._id && target._id) return String(row._id) === String(target._id);
      if (row.reciept && target.reciept) return row.reciept === target.reciept;
      if (row._id || target._id || row.reciept || target.reciept) return false;
      return row.taxYear === target.taxYear
        && row.taxId === target.taxId
        && row.protest === target.protest;
    },
    dataFromCommonDropDowns(val, whichField) {
      this.editedItem[whichField] = val;
    },
    addComments() {
      // Reset comment data to default values
      this.commentData = {
        category: 7, // Default category for user comments
        reasonCode: null,
        addToLegal: false,
        sendToWebsite: false,
        rollToNextYear: false,
        comment: '',
      };
      this.commentDialog = true;
    },
    async handleBulkCommentSave() {
      if (!this.commentData.comment || !this.commentData.comment.trim()) {
        this.flashWarn('Please enter a comment');
        return;
      }

      // Prepare bulk comment data for all tax records
      const taxRecords = ((this.editedItem || {}).taxDetails || [])
        .filter((item) => item.taxYear && item.taxId);

      if (taxRecords.length === 0) {
        this.flashWarn('No valid tax records found to add comments');
        return;
      }

      try {
        // Create comment entries for each tax record
        const commentEntries = taxRecords.map((taxRecord) => ({
          taxYear: amountToNumber(taxRecord.taxYear),
          taxId: amountToNumber(taxRecord.taxId),
          category: this.commentData.category,
          reasonCode: this.commentData.reasonCode,
          addToLegal: this.commentData.addToLegal,
          sendToWebsite: this.commentData.sendToWebsite,
          rollToNextYear: this.commentData.rollToNextYear,
          comment: this.commentData.comment,
          userName: this.userName,
          taxInquiryTransaction: taxRecord.taxInquiryId,
        }));
        await this.bulkUpsertComments({ item: commentEntries });

        this.commentDialog = false;
        // Reset comment data
        this.commentData = {
          category: 7,
          reasonCode: null,
          addToLegal: false,
          sendToWebsite: false,
          rollToNextYear: false,
          comment: '',
        };
      } catch (error) {
        console.error('Error saving bulk comments:', error);
        this.flashWarn('There was a problem saving the comments');
      }
    },
    closeCommentDialog() {
      this.commentDialog = false;
      // Reset comment data
      this.commentData = {
        category: 7,
        reasonCode: null,
        addToLegal: false,
        sendToWebsite: false,
        rollToNextYear: false,
        comment: '',
      };
    },
    // Cleanup method called when component is about to be destroyed or dialog closes
    resetStoreData() {
      // strategy - Clear CRUD store data on close rather than on load
      this.setItems([this.baseCrudKey, []]);
      this.setCriteria([this.baseCrudKey, {}]);

      // Reset component-level state
      this.serverTotals = {
        totalTaxAmt: 0,
        totalDue: 0,
        totalPenalty: 0,
        totalFees: 0,
        totalRecords: 0,
      };
      // staged rows went with the "Add Tax Details to Payment" card
      // this.newTaxDetailsToAdd = [];
      // a row held for an unanswered warning must not follow the dialog to the next payment
      this.pendingTaxRows = [];
      // nor may the previous payment's unpaid scan - its parcels are not this payment's
      this.unpaidScanCache = {};
      this.halfPaymentFlag = false;
      // the next payment queries its own pick list - see pickListLoaded
      this.pickListLoaded = false;
    },
    /**
     * Puts the grid back to the payment that is only now being entered - empty for one started
     * on the payment screen, and holding whatever rows were seeded onto it for one started from
     * a tax inquiry, which picks the records first and opens the dialog on them.
     * beforeDestroy does the same clear when the component goes, but a new payment never
     * unmounts it: the payment dialog is reused between payments, and this grid's :key only
     * changes once the payment has a number. Without this, the second payment of the day would
     * open on the rows of the first.
     */
    resetForNewPayment() {
      this.resetStoreData();
      this.warningMessage = [];
      this.showOrHideWarningMessage = false;
      // the footer describes the whole payment again until a row on THIS one is clicked
      this.clickedRow = null;
      // ...and this payment's unpaids have not been offered yet, whatever the last one's were
      this.autoLoadedRecordIds = [];
      this.syncRowsFromPayment((this.editedItem || {}).taxDetails);
      this.priceSeededRows();
    },
    /**
     * Prices the rows a payment arrived with.
     * A payment started from a tax inquiry - Pay Now on the taxroll / owner search, the TR work
     * file - seeds its rows with the tax year, id and owner name only: taxAmt, penaltyAmt and
     * total are deliberately null (CommonPaymentDialog.handlePaymentDialog), because the grid is
     * what resolves each record and prices it on the payment's penalty date. Without this pass
     * those rows sit at 0.00. dialogTaxDetails did it in fromOtherSourcesUpdateEditedItem; this
     * is the same pass over the list view's rows, through the shared row math.
     * The warnings are the ones the keyed lookups raise, collected across every row into the one
     * popup, and raised after the rows are committed rather than instead of it: these rows are
     * already on the payment, so Cancel means abandoning the payment - which is what it does -
     * not dropping a row that was never taken.
     */
    async priceSeededRows() {
      const rows = this.items(this.baseCrudKey) || [];
      // Protested Tax refund/credit rows arrive with the amounts that screen worked out. Pricing
      // them would overwrite taxAmt with the roll record's Total Due (negative on an overpaid
      // protest record), so they are left as they came.
      const needsPricing = (row) => !!(row && row.isFromOtherSources && row.taxYear && row.taxId
        && !isProtestRefundOrCredit(row));
      if (!rows.some(needsPricing)) return;
      this.loading = true;
      const penaltyEffectiveDate = this.getPenaltyEffectiveDate();
      const allWarnings = [];
      const pricedRows = [];
      /**
       * The parcel the unpaid scan asks about for a payment that arrived seeded - Pay Now on the
       * taxroll / owner search, the TR work file. The last of them, not each: this is the record
       * the clerk is paying now, the same one dialogTaxDetails scanned for when it reached the
       * last seeded row (fromOtherSourcesUpdateEditedItem passes 'lastIndex' only there).
       * A pick-list or protest load is seeded the same way and is deliberately left out - those
       * rows are a selection already made, not a record being keyed, and dialogTaxDetails sent
       * them down forExemptAndProtestFunc, which never scanned either.
       */
      let payingNowBaseId = null;
      const isASelectionAlreadyMade = (row) => !!(row.isFromProtestOrPicklist
        || row.isFromProtest || row.isExempt);
      try {
        for (let i = 0; i < rows.length; i += 1) {
          const row = rows[i];
          if (needsPricing(row)) {
            const criteria = buildTaxLookupCriteria(row.taxYear, row.taxId);
            // Sequential: each row costs a taxinquiry query plus a fee query
            // (buildTaxRowFromInquiry), and firing a whole selection's worth in parallel would
            // hammer it - same reason the parcel load below runs one at a time.
            // eslint-disable-next-line no-await-in-loop
            const newData = criteria ? await this.getTaxInquiryForPayment({ criteria }) : [];
            const resolved = (newData || [])[0];
            if (resolved) {
              // eslint-disable-next-line no-await-in-loop
              const pricedRow = await buildTaxRowFromInquiry({
                newData: resolved,
                // the seeded row carries the values the payment was opened with - its _id,
                // receipt, protest and tax type - and the lookup fills in the money
                baseRow: { ...row, taxId: resolved.taxId, isFromOtherSources: false },
                penaltyEffectiveDate,
                taxSettingItem: this.taxSettingItem,
                paymentSettingItem: this.paymentSettingItem,
                dialogHalf: (this.halfOrPostmark || {}).dialogHalf,
                loadTaxRollFeesTable: this.loadTaxRollFeesTable,
              });
              // eslint-disable-next-line no-await-in-loop
              const { warnings, thereIsAPrevUnptRec } = await this
                .collectWarningsForRecord(resolved, { isFromProtest: !!row.isFromProtest });
              allWarnings.push(...warnings);
              pricedRows.push({ ...pricedRow, thereIsAPrevUnptRec });
              if (!isASelectionAlreadyMade(row)) payingNowBaseId = resolved.baseId;
            } else {
              // the record is gone or unreadable - say so rather than leaving a silent 0.00 row
              this.flashWarn(`Tax Yr ${row.taxYear} Tax ID ${row.taxId} could not be loaded`);
              pricedRows.push(row);
            }
          } else {
            pricedRows.push(row);
          }
        }
        this.commitPricedRows(pricedRows);
        this.showTaxRowWarnings(allWarnings);
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in priceSeededRows', warn);
      } finally {
        this.loading = false;
        this.scanForUnpaidTaxes([payingNowBaseId]);
      }
    },
    /**
     * Replaces the whole row set - what pricing produces - across the grid, the payment and the
     * parent dialog. appendTaxRows is the add-to-what-is-there counterpart; this one recomputes
     * the totals from the rows, because every row's value has just changed.
     */
    commitPricedRows(rows) {
      const updatedEditedItem = { ...this.editedItem, taxDetails: rows };
      this.setItem([this.parentCrudKey, updatedEditedItem]);
      this.setItem([this.baseCrudKey, updatedEditedItem]);
      // the parent payment lives under its own crud key, so the emit - not the writes above -
      // is what gets the priced rows onto the payment being saved
      this.$emit('taxDetailsChanged', rows);
      this.syncRowsFromPayment(rows);
    },
    // Create footer totals array based on headers and calculated totals
    footerTotals() {
      const totals = [];
      const calculated = this.calculatedTotals;
      // Use totalRecords from API if available, otherwise current page count
      const rowCount = calculated.totalRecords
        || (this.items(this.baseCrudKey) || []).length;

      this.headers.forEach((header, index) => {
        switch (header.value) {
          case 'taxId':
            totals[index] = 'Totals';
            break;
          case 'ownerName':
            totals[index] = `#${rowCount}`;
            break;
          case 'taxAmt':
            totals[index] = calculated.taxAmt.toFixed(2);
            break;
          case 'penaltyAmt':
            totals[index] = calculated.penaltyAmt.toFixed(2);
            break;
          case 'totalFees':
            totals[index] = calculated.totalFees.toFixed(2);
            break;
          case 'total':
            // Show calculated total from table data
            totals[index] = calculated.total.toFixed(2);
            break;
          default:
            totals[index] = null;
            break;
        }
      });
      return totals;
    },
    // Check for mortgage codes in tax details and build warning messages
    checkForMortgageCodes(taxDetails) {
      const mtgCodeWarnings = [];
      this.mtgCodeErrorList = [];

      if (!this.paymentSettingItem.warnWhenPayingRecWithMtg) {
        return mtgCodeWarnings;
      }

      (taxDetails || []).forEach((item) => {
        if (item.mtgCode && item.mtgCode.trim() !== '') {
          mtgCodeWarnings.push(
            `Tax Yr ${item.taxYear} Tax ID ${item.taxId} is coded with the mortgage code ${item.mtgCode}.`,
          );
          this.mtgCodeErrorList.push({
            taxYear: item.taxYear,
            taxId: item.taxId,
            mtgCode: item.mtgCode,
          });
        }
      });

      return mtgCodeWarnings;
    },
    async loadLargePaymentData() {
      // Use internal values if available, otherwise fall back to props
      const currentFiscalYear = this.internalFiscalYear || this.fiscalYear;
      const currentPaymentNumber = this.internalPaymentNumber || this.paymentNumber;

      // be double-sure largePaymentList items are clear for UI/UX purposes
      this.setItems([this.baseCrudKey, []]);
      if (!currentFiscalYear || !currentPaymentNumber) {
        return;
      }
      if (this.skipNextApiCallForLargePaymentsFlag) {
        return;
      }
      try {
        this.loading = true;
        const criteria = {
          fiscalYear: Number(currentFiscalYear),
          paymentNumber: Number(currentPaymentNumber),
          skip: this.criteriaState.skip || 0,
          limit: this.criteriaState.limit || 100,
        };
        const res = await this.loadLargePayments({
          baseCrudKey: this.baseCrudKey,
          criteria,
          // fields: ['*', 'taxpayer { * }', 'taxDetails { * }'],
          postProcessTrans: this.postProccessData,
        });
        // Initialize the item structure if it doesn't exist
        if (res && !res.paymentDetails) {
          res.paymentDetails = [];
        }

        // Store server-side totals from API (not affected by pagination)
        if (res) {
          this.serverTotals = {
            totalTaxAmt: parseFloat(res.totalTaxAmt) || 0,
            totalDue: parseFloat(res.totalDue) || 0,
            totalPenalty: parseFloat(res.totalPenalty) || 0,
            totalFees: parseFloat(res.totalFees) || 0,
            totalRecords: parseInt(res.totalRecords, 10) || 0,
          };

          // Update pagination with server items length for proper pagination
          if (this.serverTotals.totalRecords > 0) {
            const paginationItem = {
              itemsperpage: this.criteriaState.limit || 100,
              serverItemsLength: this.serverTotals.totalRecords,
              footerProps: this.gridFooterProps,
            };
            this.setPaginationItems([this.baseCrudKey, paginationItem]);
          }
        }

        // Preserve fiscalYear and paymentNumber to prevent component recreation loop
        // (slight performance improvement, possibly helps fix the payment flashing issue)
        const fiscalYearToUse = this.editedItem.fiscalYear === res.fiscalYear
          ? this.editedItem.fiscalYear
          : res.fiscalYear;

        const paymentNumberToUse = this.editedItem.paymentNumber === res.paymentNumber
          ? this.editedItem.paymentNumber
          : res.paymentNumber;

        // the grid rows and the payment's taxDetails are the same rows - both have to carry the
        // audit baseline, since TaxEditDialog edits the row it was handed from the grid
        const stampedTaxDetails = this.stampAuditBaseline(res.taxDetails);
        this.setItems([this.baseCrudKey, stampedTaxDetails]);
        const preservedItem = {
          ...res,
          taxDetails: stampedTaxDetails,
          fiscalYear: fiscalYearToUse,
          paymentNumber: paymentNumberToUse,
        };
        this.setItem([this.baseCrudKey, preservedItem]);
        this.editedItem = preservedItem;
      } catch (error) {
        // Handle error appropriately
        this.$store.dispatch('flashError', 'Failed to load payment data');
      } finally {
        this.loading = false;
      }
    },
    async handleLoadCRUDList() {
      // Skip API call if flag is set
      if (this.skipNextApiCallForLargePaymentsFlag) {
        // Don't reset flag here, let it be reset after the operation completes
        return {};
      }

      /**
       * A payment being entered has nothing to load: its rows are the ones the header's Tax Yr /
       * Tax ID and Parcel ID lookups appended, and they live only in this grid until it is saved.
       * Falling through would run the picklist query on the picklist's criteria, which has
       * nothing to do with this payment - and would throw the keyed rows away.
       * Ahead of the warning reset too: this fires on every pagination / sort change, and a
       * lookup's warnings are answered by the clerk, not cleared out from under them.
       */
      if (this.isNewPayment) return {};

      /**
       * A pick list is queried once per dialog session and then belongs to the browser: the whole
       * selection is in the grid, which pages and sorts it itself (gridServerItemsLength), so a
       * page or sort change has nothing to fetch. Re-querying would overwrite the grid rows AND
       * the payment's taxDetails with what the database says, losing every amount the clerk has
       * keyed but not yet saved - see pickListLoaded.
       * Ahead of the warning reset, like the new-payment guard above, so paging does not clear a
       * popup the clerk is part-way through, nor raise the status-code and mortgage-code warnings
       * at them a second time.
       */
      if (this.pickListLoaded) return {};

      // Clear previous warning messages to prevent duplicates
      this.warningMessage = [];
      this.showOrHideWarningMessage = false;

      // For pagination mode, always call API on page/limit change
      if (this.fiscalYear && this.paymentNumber) {
        await this.loadLargePaymentData();
      } else {
        const getCriteria = this.criteria(this.parentCrudKey);
        delete getCriteria.skip;
        delete getCriteria.limit;
        delete getCriteria.isItFromProfile;
        if (getCriteria.halfPayment === true) {
          this.setHalfOrPostmark({ res: true, whichOne: 'dialogHalf' });
        }

        try {
          const res = await this.pickListLargePayments({
            baseCrudKey: this.baseCrudKey,
            criteria: getCriteria,
            postProcessTrans: this.postProccessData,
          });
          if (res.warningMessage && (res.warningMessage || []).length > 0) {
            this.showOrHideWarningMessage = true;
            this.warningMessage = Array.isArray(res.warningMessage)
              ? res.warningMessage
              : [res.warningMessage];
          }

          // Check for mortgage codes in the loaded tax details
          if (res.taxDetails && res.taxDetails.length > 0) {
            const mtgWarnings = this.checkForMortgageCodes(res.taxDetails);
            if (mtgWarnings.length > 0) {
              this.showOrHideWarningMessage = true;
              // If there's already a warning message, combine them
              if (this.warningMessage && (this.warningMessage || []).length > 0) {
                // If warningMessage is a string, convert to array
                const existingMessages = Array.isArray(this.warningMessage)
                  ? this.warningMessage
                  : [this.warningMessage];
                this.warningMessage = [...existingMessages, ...mtgWarnings];
              } else {
                this.warningMessage = mtgWarnings;
              }
            }
          }

          // Preserve fiscalYear to prevent component recreation loop
          // (slight performance improvement, possibly helps fix the payment flashing issue)
          const fiscalYearToUse = this.editedItem.fiscalYear === res.fiscalYear
            ? this.editedItem.fiscalYear // Same value, use existing ref
            : res.fiscalYear; // Different value, allow update

          // same as the paginated path: the rows carry their own audit baseline
          const stampedTaxDetails = this.stampAuditBaseline(res.taxDetails);
          this.setItems([this.baseCrudKey, stampedTaxDetails]);
          const currentItem = {
            ...this.editedItem,
            taxDetails: stampedTaxDetails,
            fiscalYear: fiscalYearToUse,
            workstation: Number(this.editedItem.workstation) || null,
          };
          if (currentItem.halfPaymentFlag) {
            this.halfPaymentFlag = true;
          }
          this.setItem([this.parentCrudKey, currentItem]);

          // Initialize serverTotals for picklist mode from the loaded data
          const taxDetails = res.taxDetails || [];
          const taxItems = taxDetails.filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND');
          this.serverTotals = {
            totalTaxAmt: taxItems.reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
            totalDue: taxItems.reduce((sum, item) => sum + amountToNumber(item.total), 0),
            totalPenalty: taxItems.reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
            totalFees: taxItems.reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
            totalRecords: taxDetails.length,
          };

          // Update pagination for picklist mode
          const paginationItem = {
            itemsperpage: this.getSettings.itemsperpage || 100,
            serverItemsLength: this.gridServerItemsLength(),
            footerProps: this.gridFooterProps,
          };
          this.setPaginationItems([this.baseCrudKey, paginationItem]);

          // The selection is in the browser now: every later page / sort change renders these
          // rows rather than asking for them again. Not set when the query failed - the action
          // swallows the error and hands back no taxDetails - so a retry is still possible.
          if (Array.isArray(res && res.taxDetails)) this.pickListLoaded = true;
        } catch (error) {
          console.error('Error reloading tax details with new penalty date:', error);
        }
      }
      return {};
    },
    editItem(item) {
      const currentItems = this.items(this.baseCrudKey) || [];
      this.editedIndex = Array.isArray(currentItems) ? currentItems.indexOf(item) : -1;
      this.editedItem = { ...item };
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.$nextTick(() => {
        this.editedItem = {};
        this.editedIndex = -1;
      });
    },
    async saveItem(item) {
      try {
        // Here you would typically make an API call to save the item
        // For now, we'll just update the local data
        const currentItems = this.items(this.baseCrudKey) || [];
        if (this.editedIndex > -1 && Array.isArray(currentItems)) {
          // Update existing item in the store
          const updatedItems = [...currentItems];
          updatedItems[this.editedIndex] = item;
          this.setItems([this.baseCrudKey, updatedItems]);
        }
        // Show success message
        this.$store.dispatch('flashSuccess', 'Tax payment updated successfully');

        // Optionally reload data to get fresh data from server
        // await this.loadLargePaymentData();
      } catch (error) {
        this.$store.dispatch('flashError', 'Failed to save tax payment');
      }
    },
    handleTaxDetailChanged(updatedItem) {
      // Set flag to prevent API call
      this.guardLocalRowWrite();

      // Define allowed fields based on GraphQL schema
      const allowedFields = [
        '_id',
        'taxYear',
        'taxId',
        'schoolDistrict',
        'taxAmt',
        'penaltyAmt',
        'mailFee',
        'lienFee',
        'advFee',
        'otherFee',
        'total',
        'feeNumber',
        'void',
        'isItNewVoidedRecord',
        'isItFromPaymentTaxDetails',
        'itsNewRecord',
        'protest',
        'posted',
        'totalDue',
        'reciept',
        'taxType',
        // display-only (Tax Type column); not in taxDetailsInputFields, so
        // upsertNOverPaymentsCommon strips it before the upsert
        'propertyTaxType',
        'fees',
        'statusCode',
        'totalFees',
        'grossAssessed',
        'exemptions',
        'ownerName',
        'originalTaxId',
        'originalTaxAmount',
        'tempVariableVoid',
        'initialTaxAmt',
        'initialPenaltyAmt',
        'initialTotalFees',
        'initialFees',
        'initialTaxYear',
        'initialTaxId',
        'AIsVisible',
        'auditSummary',
        'Half',
        'disableHalf',
        // Transient lookup values TaxEditDialog repopulates when the clerk retargets a row's
        // Tax Yr / Tax ID. Kept client-side because the display columns (certNumber), the
        // penalty recalc (recalculatePenaltiesForPicklist) and the mtg warning
        // (checkForMortgageCodes) all read them back. Safe to carry: upsertNOverPaymentsCommon
        // deletes every one of these before the upsert, so none reach the GraphQL payload.
        'certNumber',
        'taxInquiryId',
        'totalTax',
        'totalPaid',
        'taxTotalDue',
        'penaltyStartDate',
        'penaltyChangedManually',
        'allFeesList',
        'mtgCode',
        'mobileHome',
        // read by the decal prompt (PaymentCommonFunc.mixin mobileHomeArr), which names the record
        // by them; stripped there before the upsert, same as mobileHome
        'mhVin',
        'mhDecal',
        'baseId',
        'originalTaxAmt',
        'originalGrossAssessed',
        'allowBackTaxEdit',
      ];

      // Filter updatedItem to only include allowed fields
      const validateAndFilterFields = (item) => {
        const filteredItem = {};
        allowedFields.forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(item, field)) {
            filteredItem[field] = item[field];
          }
        });
        // Explicitly remove Vuetify's internal selection tracking property
        delete filteredItem.selected;
        return filteredItem;
      };

      // Validate and filter the updated item
      const validatedUpdatedItem = validateAndFilterFields(updatedItem);

      // Find the original item before update (for calculating difference in totals)
      const currentTaxDetails = this.items(this.baseCrudKey) || [];
      let originalItem = null;

      // Replace the FIRST row this describes and no other. The old version replaced every row
      // matching taxYear/taxId/protest, so on a payment that carried a parcel twice a void from
      // TaxEditDialog could land on the wrong line or on both - and it wrote the grid and the
      // payment record from two separately-computed arrays, which is how a save could go out
      // still carrying the pre-void row.
      const updatedTaxDetails = currentTaxDetails.map((item) => {
        // eslint-disable-next-line no-param-reassign
        item.originalTaxId = item.taxId;
        if (!originalItem && this.isSameTaxRow(item, validatedUpdatedItem)) {
          originalItem = { ...item };
          return { ...validatedUpdatedItem };
        }
        return item;
      });

      // One write, both stores, one emit - see commitTaxRows.
      this.commitTaxRows(updatedTaxDetails, {
        addedItems: originalItem ? [validatedUpdatedItem] : [],
        deletedItems: originalItem ? [originalItem] : [],
      });
    },
    /**
     * An editable cell changed on a line - the Type, which is the only one this grid renders as
     * a dropdown. Changing it here does everything changing it inside TaxEditDialog does: the
     * audit summary and the 'A' flag are settled through the shared applyManualChangesToRow (so
     * a refund / credit / transfer line carries neither, exactly as it did when the type was
     * picked first on the old dialogTaxDetails grid), the row is committed so the payment saves
     * it, and the footer / dialog totals are redone off the changed row's type.
     *
     * Totals need no arithmetic here: commitTaxRows hands the before and after rows to
     * updateLocalTotalsAndPagination, which counts each by its own type - so 1 -> 2 is a wash
     * (both count toward the payment) while 1 -> 3 takes the line back out of the total.
     */
    handleRowFieldChange(row, field, value) {
      // Type is the only inlineSelect column on this grid
      if (field !== 'taxType' || !row || !value) return;
      // belt and braces - the cell is already read-only on these rows
      if (row.posted || row.void || row.tempVariableVoid) return;
      if ((row.taxType || DEFAULT_TAX_PAYMENT_TYPE) === value) return;

      // eslint-disable-next-line max-len
      const taxAmountChangesCreateAudit = (this.paymentSettingItem || {}).taxAmountChangesCreateAudit || false;
      const settled = applyManualChangesToRow({
        row: { ...row, taxType: value },
        paymentTransaction: this.item('paymentTransactionList'),
        taxAmountChangesCreateAudit,
      });
      // Only the three values the type change actually settles are taken off it - the row keeps
      // everything else it carries (the red previous-payment highlight, the protest and exempt
      // flags, the lookup values TaxEditDialog reads back). Named rather than spread so nothing
      // the helper adds for TaxEditDialog's own benefit - previousTaxAmt, which that path strips
      // again through its allowedFields filter - can ride out to the upsert from here.
      const retyped = {
        ...row,
        taxType: value,
        auditSummary: settled.auditSummary,
        AIsVisible: settled.AIsVisible,
        grossAssessed: settled.grossAssessed,
      };
      // An unsaved row's _id carries its type (newTaxRowId), so a retyped one has to be re-keyed
      // - otherwise adding the same record back under the type this row used to be would collide
      // with it, and the two lines would answer to the same key.
      if (row.itsNewRecord && isNewTaxRowId(row._id)) {
        retyped._id = newTaxRowId({
          taxYear: retyped.taxYear,
          taxId: retyped.originalTaxId || retyped.taxId,
          protest: retyped.protest,
          taxType: value,
        });
      }

      // Replace the FIRST row this describes and no other - same rule, and the same reason, as
      // handleTaxDetailChanged: a payment may carry one parcel on more than one line.
      let originalItem = null;
      const updatedTaxDetails = (this.items(this.baseCrudKey) || []).map((item) => {
        if (!originalItem && this.isSameTaxRow(item, row)) {
          originalItem = item;
          return retyped;
        }
        return item;
      });
      if (!originalItem) return;

      this.commitTaxRows(updatedTaxDetails, {
        addedItems: [retyped],
        deletedItems: [originalItem],
      });
    },
    /**
     * Whether a line can simply be taken off the payment: only one the county has not committed
     * to. Posted, voided and receipted lines are already on the books - those are Voided from
     * their record, not removed. TaxEditDialog's trash can carries the same rule.
     */
    isTaxRowRemovable(row) {
      return !!row && !(row.posted || row.void || row.reciept);
    },
    /**
     * A row's trash can. Removes that one line the way TaxEditDialog's does - through
     * handleTaxDetailDeleted - so the totals, the audit and commitTaxRows behave the same, minus
     * the double-click and the dialog.
     */
    handleRowIconClick(row, column) {
      if (column !== 'removeRow' || !this.isTaxRowRemovable(row)) return;
      this.handleTaxDetailDeleted(row);
    },
    handleTaxDetailDeleted(deletedItem) {
      const currentTaxDetails = this.items(this.baseCrudKey) || [];

      // Same first-match-only rule as handleTaxDetailChanged: deleting one of two lines for a
      // parcel must not take both.
      let removed = false;
      const updatedTaxDetails = currentTaxDetails.filter((item) => {
        if (!removed && this.isSameTaxRow(item, deletedItem)) {
          removed = true;
          return false;
        }
        return true;
      });

      this.commitTaxRows(updatedTaxDetails, { deletedItems: removed ? [deletedItem] : [] });
    },
    isAllTaxDetailsPosted() {
      // Check if all tax details in editedItem are posted
      const taxDetails = (this.editedItem && this.editedItem.taxDetails) || [];
      if (taxDetails.length === 0) {
        return false; // Show dialogTaxDetails if no items
      }
      // Return false if NOT all posted (show dialogTaxDetails for unposted items)
      const allPosted = taxDetails.every((item) => item.posted === true);
      return allPosted;
    },
    /**
     * Stamps the audit baseline - the "copies" applyAuditForPayment compares each row against -
     * on rows that came off a saved payment. Without it an untouched row audits its own penalty
     * as a change ("Penalty changes from 0.00 to X") and every fee on it as newly added, because
     * amountToNumber(undefined) is 0.
     * paymentTransactionList does exactly this for the payment screen when a transaction is
     * selected (handleSingleOrArrowClick); the rows this grid loads had no equivalent, which is
     * why editing a row on a saved payment raised a spurious audit record.
     * Rows added by the header Tax Yr / Tax ID or Parcel ID lookups arrive already stamped by
     * taxRowLookup.util, so a row that carries a baseline keeps its own.
     */
    stampAuditBaseline(taxDetails) {
      return (taxDetails || []).map((row) => {
        if (row.initialPenaltyAmt !== undefined) return row;
        return {
          ...row,
          initialPenaltyAmt: amountToNumber(row.penaltyAmt),
          initialTaxAmt: amountToNumber(row.taxAmt),
          // a copy, not the same array - sharing it would let a later fee edit mutate the
          // baseline too, and the audit would miss the change
          initialFees: (row.fees || []).map((fee) => ({ ...fee })),
          initialTotalFees: amountToNumber(row.totalFees),
          initialTaxYear: row.taxYear,
          initialTaxId: row.taxId,
        };
      });
    },
    /**
     * The warnings a resolved record raises before its row is committed - the same set the
     * payment screen's commonLoadTaxInquiryFunc raises (status codes, mortgage code, no taxes
     * due, an existing unposted payment). Costs one extra query per record for the unposted
     * payment check, plus a second only for a 416/419 record.
     */
    async collectWarningsForRecord(resolved, { isFromProtest = false } = {}) {
      const { taxYear, taxId } = resolved;
      // A protest refund and its release credits sit next to each other on the same record by
      // design, so for a protest load 416/419 is dropped entirely; other codes still show.
      const alertStatusCodes = isFromProtest
        ? partitionPendingPaymentAlertCodes(resolved.alertStatusCodes).otherCodes
        : resolved.alertStatusCodes;
      const fiscalYear = (this.editedItem || {}).fiscalYear || this.fiscalYear;
      let existingPayments = [];
      let pendingPaymentDetails = [];
      try {
        existingPayments = await this.loadPayment({
          criteria: {
            taxYear, taxId, fiscalYear, void: false, posted: false,
          },
        }) || [];
        if (hasPendingPaymentAlertCode(alertStatusCodes)) {
          pendingPaymentDetails = await this.getPendingPaymentDetails({
            criteria: { pairs: [{ taxYear, taxId }] },
          }) || [];
        }
      } catch (warn) {
        // a warning lookup that fails must not stop the row itself from loading
        console.warn('Warning: Something unexpected occurred in collectWarningsForRecord', warn);
      }
      return {
        warnings: collectTaxRowWarnings({
          newData: { ...resolved, alertStatusCodes },
          existingPayments,
          pendingPaymentDetails,
          paymentSettingItem: this.paymentSettingItem,
        }),
        // drives the red row highlight, same as the payment screen's thereIsAPrevUnptRec
        thereIsAPrevUnptRec: !!existingPayments.length,
      };
    },
    /**
     * Raises the collected warnings in the popup this component already shows for its load-time
     * warnings, so one dialog carries everything a lookup found - including a whole parcel's
     * worth of records.
     */
    showTaxRowWarnings(warnings) {
      if (!(warnings || []).length) return;
      this.warningMessage = [...(this.warningMessage || []), ...warnings];
      this.showOrHideWarningMessage = true;
    },
    /**
     * A clean lookup goes straight into the grid. One that raised a warning is held back until
     * the clerk answers the popup - Continue commits the rows, Cancel drops them - so backing
     * out of a warning leaves nothing behind on the payment.
     */
    commitOrHoldTaxRows(newRows, warnings) {
      if (!(warnings || []).length) {
        this.appendTaxRows(newRows);
        return;
      }
      this.pendingTaxRows = [...this.pendingTaxRows, ...newRows];
      this.showTaxRowWarnings(warnings);
    },
    /**
     * Penalty / fee effective date for a row keyed in the header. Same precedence TaxEditDialog
     * uses: the payment's own penalty date if it has one, else today, with Postmark Ok pulling
     * it back to the end of the previous penalty period.
     */
    getPenaltyEffectiveDate() {
      let penDate = toIsoDate(this.todaysDate);
      // the payment's own key first - the crud store hands back an empty object for a key that
      // was never written, so the old picklist-first lookup could never fall through to it
      const parentPayment = this.item(this.parentCrudKey)
        || this.item('paymentTransactionList') || {};
      if (parentPayment.penaltyDate) {
        penDate = toIsoDate(parentPayment.penaltyDate);
      }
      if ((this.halfOrPostmark || {}).dialogPostmark) {
        penDate = getPreviousPenaltyPeriodEnd(this.todaysDate, this.taxSettingItem.penaltyDate);
      }
      return penDate;
    },
    /**
     * Resolves the Tax Yr + Tax ID keyed in the payment dialog header and appends the matching
     * tax record to this grid, priced on the payment's penalty date. This is how rows are added
     * here now that the "Add Tax Details to Payment" card (and its nested dialogTaxDetails) is
     * commented out.
     * A Tax ID of 8+ digits is treated as a parcel ID, exactly as the payment screen does.
     * Returns true when a row was added, so the header can clear its fields; a miss or a
     * duplicate leaves them keyed for correcting.
     * `taxType` is the type keyed in the band's Type selector - 1 Tax Payment unless the clerk
     * picked another one, which is how a refund is entered as a refund rather than as a payment
     * that has to be corrected afterwards.
     */
    async addTaxRowByYearAndId({ taxYear, taxId, taxType } = {}) {
      const criteria = buildTaxLookupCriteria(taxYear, taxId);
      // guards an incomplete pair and a re-entry while the first lookup is still in flight
      if (!criteria || this.taxRowLookupLoading) return false;
      this.taxRowLookupLoading = true;
      // the parcel of the record this keying turns out to be - asked about below, once the
      // lookup has let go of taxRowLookupLoading, because 'Load Unpaids Automatically' refuses
      // to load anything while a lookup is in flight and nothing would wake it again
      let scannedBaseIds = [];
      try {
        const newData = await this.getTaxInquiryForPayment({ criteria });
        if (!(newData || []).length) {
          this.flashWarn('Record with combination of taxyear and taxid not found');
          return false;
        }
        const resolved = newData[0];
        const rowTaxType = taxType || DEFAULT_TAX_PAYMENT_TYPE;
        const currentItems = this.items(this.baseCrudKey) || [];
        const alreadyOnPayment = currentItems.some(
          (row) => isSameRecordAndType(row, resolved, rowTaxType),
        );
        if (alreadyOnPayment) {
          this.flashWarn(`Tax ID ${resolved.taxId} is already added for tax year ${resolved.taxYear}`);
          return false;
        }
        const newRow = await buildTaxRowFromInquiry({
          newData: resolved,
          // A fresh row keyed the way the tax-details grid staged one: still new, and carrying
          // the type the band was set to. itsNewRecord + originalTaxId (set by the util from the
          // resolved record) are what the upsert reads for the taxId, which is why a parcel-ID
          // search still saves against the right tax record.
          baseRow: {
            taxId: resolved.taxId,
            taxType: rowTaxType,
            itsNewRecord: true,
            posted: false,
            void: false,
            selected: false,
            protest: false,
          },
          penaltyEffectiveDate: this.getPenaltyEffectiveDate(),
          taxSettingItem: this.taxSettingItem,
          paymentSettingItem: this.paymentSettingItem,
          dialogHalf: (this.halfOrPostmark || {}).dialogHalf,
          loadTaxRollFeesTable: this.loadTaxRollFeesTable,
        });
        const { warnings, thereIsAPrevUnptRec } = await this.collectWarningsForRecord(resolved);
        this.commitOrHoldTaxRows([{ ...newRow, thereIsAPrevUnptRec }], warnings);
        scannedBaseIds = [resolved.baseId];
        return true;
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in addTaxRowByYearAndId', warn);
        return false;
      } finally {
        this.taxRowLookupLoading = false;
        // not awaited: the header has been told its row is on the payment, and the parcel's
        // unpaids arrive when they arrive - scanForUnpaidTaxes swallows its own failures
        this.scanForUnpaidTaxes(scannedBaseIds);
      }
    },
    /**
     * Which of a parcel's unpaid records a Parcel ID lookup actually loads.
     * 'Load Unpaids Automatically' on - all of them, every year with a balance, which is what the
     * setting asks for.
     * Off - only the newest tax year's, the bill the clerk keyed the parcel to pay. The back years
     * stay off the payment until the row's Add Unpaids is clicked, so nothing loads - and nothing
     * warns about a pending payment or a status code - that was not asked for. They are not lost
     * sight of: the scan still finds them, so the footer flags "Other Taxes Due" and the row
     * offers the button.
     * A year carrying more than one record (real estate plus a special assessment) keeps both -
     * that is one year's bill, not a back year.
     */
    parcelRecordsToLoad(records) {
      if (this.autoLoadUnpaids || !(records || []).length) return records || [];
      const newestYear = records.reduce((year, rec) => Math.max(year, Number(rec.taxYear) || 0), 0);
      return records.filter((rec) => Number(rec.taxYear) === newestYear);
    },
    /**
     * Resolves the Parcel ID keyed in the payment dialog header and appends the parcel's unpaid
     * tax records as rows - every year with a balance, or just the newest, depending on the
     * setting (see parcelRecordsToLoad). The dialogTaxDetails version of this is
     * loadUnpaidsByParcelId, which always took every year.
     * Returns true when at least one row was added, so the header can clear the field.
     * Every row loaded carries `taxType` - the type the band was set to - so a parcel keyed with
     * Type 2 loads its records as refunds, the same as a keyed Tax Yr / Tax ID pair does.
     */
    async addUnpaidRowsByParcelId({
      parcelId: rawParcelId = '', silentWhenMissing = false, taxType,
    } = {}) {
      const parcelId = normalizeParcelId(rawParcelId);
      // guards an empty field and a re-entry while the first lookup is still in flight
      if (!parcelId || this.taxRowLookupLoading) return false;
      this.taxRowLookupLoading = true;
      // the parcel just keyed - see addTaxRowByYearAndId for why it is asked about in the finally
      let scannedBaseIds = [];
      try {
        const unpaidRecords = await this.getTaxInquiryForPayment({
          criteria: { pIdExactMatch: parcelId, unpaids: true },
        }) || [];
        if (!unpaidRecords.length) {
          // the debounced path skips the existence check too - no second query per keystroke
          if (!silentWhenMissing) await this.flashNothingToLoadForParcel(parcelId);
          return false;
        }
        // Same duplicate guard the keyed Tax Yr / Tax ID path applies, per record - and on the
        // type being loaded, so a parcel already on the payment as payments can be loaded again
        // as refunds.
        const rowTaxType = taxType || DEFAULT_TAX_PAYMENT_TYPE;
        const currentItems = this.items(this.baseCrudKey) || [];
        const alreadyOnPayment = (rec) => currentItems.some(
          (row) => isSameRecordAndType(row, rec, rowTaxType),
        );
        const recordsToLoad = unpaidRecords.filter((rec) => !alreadyOnPayment(rec));
        if (!recordsToLoad.length) {
          this.flashWarn(`Parcel ${parcelId}: all unpaid records are already on this payment`);
          return false;
        }
        const skippedCount = unpaidRecords.length - recordsToLoad.length;
        if (skippedCount) {
          this.flashWarn(`Parcel ${parcelId}: ${skippedCount} unpaid record(s) already on this payment were skipped`);
        }
        await this.appendRowsForRecords(this.parcelRecordsToLoad(recordsToLoad), rowTaxType);
        // every record this parcel owes, not just the ones loaded - with the setting off only the
        // newest year goes on the payment, and the back years are exactly what the flag and the
        // row's Add Unpaids button are for
        scannedBaseIds = unpaidRecords.map((rec) => rec.baseId);
        return true;
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in addUnpaidRowsByParcelId', warn);
        return false;
      } finally {
        this.taxRowLookupLoading = false;
        this.scanForUnpaidTaxes(scannedBaseIds);
      }
    },
    /**
     * Prices a set of resolved taxinquiry records and commits them as rows in one go - shared by
     * the header's Parcel ID lookup and the footer's Add All Unpaids, which differ only in how
     * they arrive at the records.
     * `taxType` is only ever passed by the Parcel ID lookup, which carries the band's Type.
     * Everything else here - a row's Add Unpaids, 'Load Unpaids Automatically' - is taking the
     * taxes a parcel still owes, which are payments, so those stay on the default.
     */
    async appendRowsForRecords(records, taxType = DEFAULT_TAX_PAYMENT_TYPE) {
      // neither caller's records carry a sort of their own - oldest year first, then tax id
      const recordsToLoad = [...(records || [])].sort(
        (recA, recB) => (Number(recA.taxYear) - Number(recB.taxYear))
          || (Number(recA.taxId) - Number(recB.taxId)),
      );
      if (!recordsToLoad.length) return;
      const penaltyEffectiveDate = this.getPenaltyEffectiveDate();
      const newRows = [];
      const allWarnings = [];
      for (let i = 0; i < recordsToLoad.length; i += 1) {
        const resolved = recordsToLoad[i];
        // Sequential: each row's fees are a query of their own (buildTaxRowFromInquiry ->
        // loadTaxRollFeesTable), and firing a parcel's worth in parallel would hammer it.
        // eslint-disable-next-line no-await-in-loop
        const newRow = await buildTaxRowFromInquiry({
          newData: resolved,
          baseRow: {
            taxId: resolved.taxId,
            taxType,
            itsNewRecord: true,
            posted: false,
            void: false,
            selected: false,
            protest: false,
          },
          penaltyEffectiveDate,
          taxSettingItem: this.taxSettingItem,
          paymentSettingItem: this.paymentSettingItem,
          dialogHalf: (this.halfOrPostmark || {}).dialogHalf,
          loadTaxRollFeesTable: this.loadTaxRollFeesTable,
        });
        // eslint-disable-next-line no-await-in-loop
        const { warnings, thereIsAPrevUnptRec } = await this.collectWarningsForRecord(resolved);
        allWarnings.push(...warnings);
        newRows.push({ ...newRow, thereIsAPrevUnptRec });
      }
      // one commit for the whole set, so the totals and the emit fire once - and one popup,
      // carrying every record's warnings. A set that raised any warning is held whole: the clerk
      // answers once for it, rather than per record.
      this.commitOrHoldTaxRows(newRows, allWarnings);
    },
    /**
     * Asks the parcels it is handed for their unpaid tax records, so the footer can flag "Other
     * Taxes Due" and Add Unpaids can load them. This is dialogTaxDetails.getUnpaidTaxes, with
     * the answer cached per baseId instead of held in one paymentTaxes array: the grid's rows can
     * span parcels here, and rows come and go without the parcels changing.
     * The parcels are passed in rather than read off the payment, and that is the whole of this
     * fix: what gets asked about is the record that just arrived - keyed, scanned, or seeded by
     * Pay Now - so a payment costs one query per record added however many rows it already
     * carries. Reading the payment instead meant the query count grew with the payment, which is
     * why it used to be capped, and the cap is what made 'Load Unpaids Automatically' stop
     * without a word once a payment spanned more than ten parcels. A payment loaded from the
     * server and a pick-list load ask about nothing at all: their records are already on them.
     * Records already on the payment are NOT filtered out here - unpaidTaxRecords does that
     * against the live rows, which is what keeps the flag right after an add or a delete without
     * another round of queries.
     */
    async scanForUnpaidTaxes(baseIdsToScan) {
      const asked = [...new Set((baseIdsToScan || [])
        .filter((baseId) => baseId !== null && baseId !== undefined && baseId !== '')
        .map((baseId) => Number(baseId))
        .filter((baseId) => !Number.isNaN(baseId)))];
      // a parcel already answered for, or already being asked about, is not asked again
      const unscanned = asked.filter((baseId) => !this.unpaidScanCache[baseId]
        && !this.unpaidScanInFlight.includes(baseId));
      if (!unscanned.length) return;
      this.unpaidScanInFlight = [...this.unpaidScanInFlight, ...unscanned];
      try {
        const scanned = {};
        for (let i = 0; i < unscanned.length; i += 1) {
          const baseId = unscanned[i];
          // one query per parcel, sequential for the same reason the row loads are - and a parcel
          // with nothing unpaid is cached as an empty array, so it is asked about only once
          // eslint-disable-next-line no-await-in-loop
          const unpaidRecords = await this.getTaxInquiryForPayment({
            criteria: { baseId, unpaids: true },
          }) || [];
          scanned[baseId] = unpaidRecords;
        }
        // replaced rather than mutated, so the computeds reading it re-run
        this.unpaidScanCache = { ...this.unpaidScanCache, ...scanned };
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in scanForUnpaidTaxes', warn);
      } finally {
        this.unpaidScanInFlight = this.unpaidScanInFlight
          .filter((baseId) => !unscanned.includes(baseId));
      }
    },
    /**
     * A single click on a row - what points the footer's Mtg Code and "Other Taxes Due" at it.
     * The grid also highlights the row it reports, so the clerk can see which record the footer
     * is describing.
     */
    handleRowClicked(row) {
      this.clickedRow = row || null;
    },
    /**
     * The unpaid records the scan found on one row's parcel that are not on this payment yet -
     * what that row's Add Unpaids loads. unpaidTaxRecords has already deduped the scan and
     * dropped everything the grid (or a row held behind an unanswered popup) carries, so this
     * only narrows that set to the parcel the row belongs to.
     */
    rowUnpaidRecords(row) {
      const { baseId } = row || {};
      if (baseId === null || baseId === undefined || baseId === '') return [];
      const scannedIds = (this.unpaidScanCache[Number(baseId)] || []).map((rec) => rec._id);
      return this.unpaidTaxRecords.filter((rec) => scannedIds.includes(rec._id));
    },
    /**
     * Whether that row gets a button - i.e. whether its parcel still has taxes due. A row whose
     * parcel has nothing left shows an empty cell, and so does a row whose parcel was never asked
     * about: a payment loaded from the server, or one seeded by a pick-list, is not scanned (see
     * scanForUnpaidTaxes), so its rows carry no button until a record is keyed onto it.
     * Every row of the same parcel shows the button, and clicking any of them loads the same
     * records - after which none of them do.
     * No row shows one while 'Load Unpaids Automatically' is on: there is nothing to ask for,
     * because autoLoadUnpaidRows has already taken it.
     */
    rowHasUnpaids(row) {
      if (this.autoLoadUnpaids) return false;
      return this.rowUnpaidRecords(row).length > 0;
    },
    /**
     * 'Load Unpaids Automatically': takes every record the scan has turned up and puts it on the
     * payment, in place of the per-row button the clerk would otherwise click. Runs off
     * unpaidTaxRecords rather than off the scan, so it covers every way a record can start being
     * due - the payment loading, a keyed Tax Yr / Tax ID, a parcel lookup, a row deleted and
     * re-keyed - each of which ends in that set changing.
     * Each record is tried once (autoLoadedRecordIds). A set that raises a warning is held behind
     * the popup, and if the clerk cancels it the records become due again; without remembering
     * them, this would take them straight back and re-raise the popup, forever. The footer's flag
     * still shows those, so the payment does not quietly drop them.
     */
    async autoLoadUnpaidRows() {
      if (!this.autoLoadUnpaids) return;
      const records = this.unpaidTaxRecords
        .filter((rec) => !this.autoLoadedRecordIds.includes(rec._id));
      // a load or a header lookup already in flight comes back to this through unpaidTaxRecords
      if (!records.length || this.taxRowLookupLoading) return;
      this.autoLoadedRecordIds = [
        ...this.autoLoadedRecordIds, ...records.map((rec) => rec._id),
      ];
      this.taxRowLookupLoading = true;
      try {
        await this.appendRowsForRecords(records);
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in autoLoadUnpaidRows', warn);
      } finally {
        this.taxRowLookupLoading = false;
      }
    },
    /**
     * A row's Add All Unpaids: loads that row's parcel's unpaid records. This is the footer
     * button's addAllUnpaidRows narrowed to one parcel - the clerk now picks the parcel by the
     * row they click instead of taking every parcel on the payment at once.
     */
    async addUnpaidRowsForRow(row) {
      const records = this.rowUnpaidRecords(row);
      // guards a double click and a click landing while a header lookup is still in flight
      if (!records.length || this.taxRowLookupLoading) return;
      this.taxRowLookupLoading = true;
      try {
        await this.appendRowsForRecords(records);
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in addUnpaidRowsForRow', warn);
      } finally {
        this.taxRowLookupLoading = false;
      }
    },
    /**
     * What the footer's Add All Unpaids ran - every unpaid record the scan found on this payment's
     * parcels that is not already on it. Kept because the button is only commented out, not gone.
     */
    async addAllUnpaidRows() {
      const records = this.unpaidTaxRecords;
      // guards a double click and a click landing while a header lookup is still in flight
      if (!records.length || this.taxRowLookupLoading) return;
      this.taxRowLookupLoading = true;
      try {
        await this.appendRowsForRecords(records);
      } catch (warn) {
        console.warn('Warning: Something unexpected occurred in addAllUnpaidRows', warn);
      } finally {
        this.taxRowLookupLoading = false;
      }
    },
    // Nothing was loaded - say which case it is. The existence check is one record with ids only,
    // and runs only on this path, so the common case stays a single query.
    async flashNothingToLoadForParcel(parcelId) {
      const anyRecords = await this.getTaxInquiryForPayment({
        criteria: { pIdExactMatch: parcelId, limit: 1 },
        fields: { _id: '', taxYear: '' },
      }) || [];
      if (anyRecords.length) this.flashWarn(`Parcel ${parcelId} has no unpaid taxes`);
      else this.flashWarn(`Parcel ${parcelId} not found`);
    },
    /**
     * Commits resolved rows into the grid, the payment and the parent dialog - the same three
     * writes handleTaxDetailSaved / handleTaxDetailDeleted make, so the footer totals, the
     * pagination and the dialog's Total Due all stay in step. The skip flag stops the append
     * from triggering a reload that would throw the rows away before they are saved.
     */
    appendTaxRows(newRows) {
      if (!(newRows || []).length) return;
      this.guardLocalRowWrite();
      const updatedItems = [...(this.items(this.baseCrudKey) || []), ...newRows];
      this.setItems([this.baseCrudKey, updatedItems]);
      const updatedEditedItem = {
        ...this.editedItem,
        taxDetails: updatedItems,
      };
      this.setItem([this.parentCrudKey, updatedEditedItem]);
      this.setItem([this.baseCrudKey, updatedEditedItem]);
      // the parent payment lives under its own crud key, so the emit - not the writes above -
      // is what gets the rows onto the payment being saved
      this.$emit('taxDetailsChanged', updatedItems);
      this.updateLocalTotalsAndPagination(updatedItems, newRows, []);
    },
    // Update serverTotals and pagination after local add/delete operations
    // addedItems: items being added (to sum with existing serverTotals)
    // deletedItems: items being deleted (to subtract from existing serverTotals)
    updateLocalTotalsAndPagination(allItems, addedItems = [], deletedItems = []) {
      // Calculate totals for added items
      // Include items that are TAXPAYMENT or don't have taxType set (new items)
      const addedTaxItems = (addedItems || [])
        .filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND') || [];
      const addedTotals = {
        taxAmt: addedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
        penalty: addedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
        fees: addedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
        total: addedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.total), 0),
      };

      // Calculate totals for deleted items
      // Include items that are TAXPAYMENT or don't have taxType set
      const deletedTaxItems = (deletedItems || [])
        .filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND') || [];
      const deletedTotals = {
        taxAmt: deletedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
        penalty: deletedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
        fees: deletedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
        total: deletedTaxItems
          .reduce((sum, item) => sum + amountToNumber(item.total), 0),
      };

      // If serverTotals exist from API, add/subtract from them
      if (this.serverTotals && this.serverTotals.totalRecords > 0) {
        this.serverTotals = {
          totalTaxAmt: amountToNumber(this.serverTotals.totalTaxAmt)
            + addedTotals.taxAmt - deletedTotals.taxAmt,
          totalDue: amountToNumber(this.serverTotals.totalDue)
            + addedTotals.total - deletedTotals.total,
          totalPenalty: amountToNumber(this.serverTotals.totalPenalty)
            + addedTotals.penalty - deletedTotals.penalty,
          totalFees: amountToNumber(this.serverTotals.totalFees)
            + addedTotals.fees - deletedTotals.fees,
          totalRecords: (this.serverTotals.totalRecords || 0)
            + addedItems.length - deletedItems.length,
        };
      } else {
        // No serverTotals, calculate from all items
        // Include items that are TAXPAYMENT or don't have taxType set
        const taxItems = (allItems || [])
          .filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND') || [];

        this.serverTotals = {
          totalTaxAmt: taxItems
            .reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
          totalDue: taxItems
            .reduce((sum, item) => sum + amountToNumber(item.total), 0),
          totalPenalty: taxItems
            .reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
          totalFees: taxItems
            .reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
          totalRecords: allItems.length,
        };
      }

      // Update pagination with new total records
      const paginationItem = {
        itemsperpage: this.criteriaState.limit || 100,
        serverItemsLength: this.gridServerItemsLength(),
        footerProps: this.gridFooterProps,
      };
      this.setPaginationItems([this.baseCrudKey, paginationItem]);
    },
    /**
     * Totals for a given set of rows. Separate from updateLocalTotalsAndPagination, which
     * works in deltas off the existing serverTotals - no use when the rows were rewritten
     * wholesale (Void All) rather than added to or removed from.
     */
    totalsFromRows(rows) {
      const taxItems = (rows || [])
        .filter((item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND');
      return {
        totalTaxAmt: taxItems.reduce((sum, item) => sum + amountToNumber(item.taxAmt), 0),
        totalDue: taxItems.reduce((sum, item) => sum + amountToNumber(item.total), 0),
        totalPenalty: taxItems.reduce((sum, item) => sum + amountToNumber(item.penaltyAmt), 0),
        totalFees: taxItems.reduce((sum, item) => sum + amountToNumber(item.totalFees), 0),
        totalRecords: (rows || []).length,
      };
    },
    /**
     * Re-seeds the grid from the parent payment's taxDetails and recomputes the footer from
     * them. This list keeps its OWN copy of the rows under `largePaymentList`, so a header-side
     * change that rewrites taxDetails wholesale - Void All - has to be pushed in here. Without
     * it the grid keeps rendering the live amounts, and the next thing it emits (a row edit, an
     * added row, a delete) hands those un-voided rows back to the parent and undoes the void.
     */
    syncRowsFromPayment(taxDetails) {
      const rows = [...(taxDetails || [])];
      // a purely local re-seed: there is nothing new to fetch
      this.guardLocalRowWrite();
      this.setItems([this.baseCrudKey, rows]);

      const totals = this.totalsFromRows(rows);
      // On a server-paginated payment only the current page is in `rows` - and Void All only
      // voids that page - so the money figures follow the rows we were handed while the record
      // count stays at the full count, or pagination would collapse to one page. Safe for the
      // new-payment path too: resetStoreData zeroes totalRecords just before calling this.
      this.serverTotals = {
        ...totals,
        totalRecords: Math.max(totals.totalRecords, this.serverTotals.totalRecords || 0),
      };
      this.setPaginationItems([this.baseCrudKey, {
        itemsperpage: this.criteriaState.limit || this.getSettings.itemsperpage || 100,
        serverItemsLength: this.gridServerItemsLength(),
        footerProps: this.gridFooterProps,
      }]);
    },
    /**
     * Re-books a fee the payment header wrote onto ONE row - Over/Under and Penny Round both put
     * their balancing fee on the first tax detail - into this grid.
     *
     * Two separate things to fix, because the footer is not a sum of the rows on screen:
     *  - the row itself. Usually the same object the payment's taxDetails holds, so it has
     *    already repainted; re-written here anyway for the case where the grid is holding a copy.
     *  - `serverTotals`, which is what the Totals row and the Running Total read. It is a running
     *    figure (on a server-paged payment only the current page is ever loaded, so it cannot be
     *    re-derived from the rows), so it is moved by the same delta rather than recomputed.
     * Without this the clerk gets a Totals row and a Running Total that disagree with the row
     * above them and with Change Due.
     */
    applyHeaderFeeChange(updatedRow, feeDelta) {
      const rows = this.items(this.baseCrudKey) || [];
      const index = rows.findIndex((row) => this.isSameTaxRow(row, updatedRow || {}));
      if (index >= 0 && rows[index] !== updatedRow) {
        const next = [...rows];
        next[index] = {
          ...rows[index],
          fees: updatedRow.fees,
          initialFees: updatedRow.initialFees,
          totalFees: updatedRow.totalFees,
          total: updatedRow.total,
        };
        // a purely local rewrite of a row we already have - nothing new to fetch
        this.guardLocalRowWrite();
        this.setItems([this.baseCrudKey, next]);
      }

      const delta = amountToNumber(feeDelta);
      if (!delta) return;
      // amountToNumber round-trips the sum: a cent-sized delta is not exact in binary, so
      // 44.50 + 0.02 lands on 44.519999999999996 and the footer would carry that forward.
      this.serverTotals = {
        ...this.serverTotals,
        totalFees: amountToNumber(amountToNumber(this.serverTotals.totalFees) + delta),
        totalDue: amountToNumber(amountToNumber(this.serverTotals.totalDue) + delta),
      };
    },
    /* Both drove the commented-out "Add Tax Details to Payment" card - there are no staged
       rows to commit without it. paymentDialog still calls addToList from
       handlePaymentFieldActivity, but behind a `typeof === 'function'` guard, so it no-ops.

    /**
     * Commits the rows staged in "Add Tax Details" now that ADD TO LIST is hidden. Reached
     * from the '+' button (commitStagedTaxRows) and from a finished unpaids / parcel load
     * (unpaidTaxesAdded, once the clerk has cleared any warning modal it raised).
     * Guarded on hasValidItemsToAdd for the same reason handlePaymentFieldActivity is:
     * addToList's empty-list `return` is commented out, so calling it with nothing complete
     * flashes both "already been added" and "0 item(s) added to payment". Both triggers can
     * legitimately fire with only a blank row staged, so this check is required.
     *|
    commitStagedTaxRows() {
      if (this.hasValidItemsToAdd) {
        this.addToList();
      }
    },
    addToList() {
      // Manually trigger adding items from newTaxDetailsToAdd to main list
      if (!this.newTaxDetailsToAdd || this.newTaxDetailsToAdd.length === 0) {
        this.$store.dispatch('flashWarn', 'No items to add.');
        return;
      }

      const baseItems = this.items(this.baseCrudKey) || [];

      // Filter items that are complete and ready to add. The duplicate check that used to live
      // here was commented out, which is what let this path append a row the newTaxDetailsToAdd
      // watcher had already committed - the two copies of 2023/9804 on Pontotoc 719. It now
      // belongs to commitTaxRows, so there is exactly ONE rule and ONE warning.
      const completeItems = this.newTaxDetailsToAdd.filter((newItem) => newItem.taxYear
        && newItem.taxId
        && newItem.ownerName);

      // Nothing resolved yet - the clerk is still typing. Re-committing the unchanged rows would
      // only churn the stores (the old code fell through and flashed a misleading warning).
      if (completeItems.length === 0) {
        return;
      }

      const { dropped } = this.commitTaxRows([...baseItems, ...completeItems], {
        addedItems: completeItems,
      });

      // Clear the staged items that were handled - whether they were added or rejected as
      // duplicates. Leaving a rejected row staged would re-offer it on the next commit.
      this.newTaxDetailsToAdd = this.newTaxDetailsToAdd.filter((item) => {
        const wasHandled = completeItems.some((handledItem) => (
          String(handledItem.taxYear) === String(item.taxYear)
          && String(handledItem.taxId) === String(item.taxId)
        ));
        return !wasHandled;
      });

      const addedCount = completeItems.length - dropped.length;
      if (addedCount > 0) {
        this.$store.dispatch('flashSuccess', `${addedCount} item(s) added to payment`);
      }
    },
    // New method to recalculate penalties for picklist payment dialog
    // Only recalculates for newly added records in the "Add Tax Details" section
    recalculatePenaltiesForPicklist(newPenaltyDate) {
      const { penaltyDate: settingPenaltyDate, penaltyRate } = this.taxSettingItem;

      // Get newly added tax details from the dialogTaxDetails component
      const newlyAddedDetails = this.newTaxDetailsToAdd || [];

      if (newlyAddedDetails.length === 0) {
        return;
      }

      // Check if any records are selected
      const selectedRecords = newlyAddedDetails.filter((item) => item.selected);

      // Recalculate penalties only for selected records (or all if none selected)
      const updatedNewDetails = newlyAddedDetails.map((item) => {
        // Check if this item should be processed
        const shouldProcess = selectedRecords.length === 0 || item.selected;

        // Skip if not in the processing list
        if (!shouldProcess) {
          return item;
        }

        // Skip if item is posted or doesn't have required data
        if (item.posted || !item.taxYear || !item.taxId) {
          return item;
        }

        const updatedItem = { ...item };
        const isExemptManufacturing = (item.statusCode || [])
          .includes(exemptManufacturingStatusCode);
        const isItAExemptPayment = updatedItem.isExempt;

        // Skip exempt payments
        if (isItAExemptPayment) {
          return item;
        }

        // Calculate penalty using the same logic as dialogTaxDetails
        const penaltyEndDate = toShortDate(newPenaltyDate);
        const penStartDate = toShortDate(item.penaltyStartDate);

        const returnedVal = penaltyCalculation(
          penaltyEndDate,
          penStartDate,
          settingPenaltyDate,
          amountToNumber(item.totalTax || item.taxAmt),
          amountToNumber(item.totalPaid || 0),
          0,
          penaltyRate,
          amountToNumber(item.totalDue || item.taxAmt),
          item.taxYear,
          (item.statusCode || []),
          this.taxSettingItem,
        );

        updatedItem.penaltyAmt = amountFormat(returnedVal.penaltyAmt);

        // Update feelist based on effective date if needed
        let totalFee = amountToNumber(item.totalFees) || 0;
        if ((item.allFeesList || []).length && !isExemptManufacturing) {
          const { feeDet, feesAmt } = getFeeListBasedOnEffeciveDate(
            item.allFeesList,
            newPenaltyDate,
          );
          totalFee = feesAmt;
          updatedItem.fees = feeDet;
          updatedItem.totalFees = amountFormat(totalFee);
        }

        // Update total amount
        const totalCalcAmt = amountToNumber(returnedVal.penaltyAmt)
          + amountToNumber(item.taxAmt)
          + amountToNumber(totalFee);
        updatedItem.total = amountFormat(totalCalcAmt || 0);

        return updatedItem;
      });

      // Update the newTaxDetailsToAdd array (v-model will sync to dialogTaxDetails)
      this.newTaxDetailsToAdd = updatedNewDetails;

      // Force recalculation of totals
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    */
    openConfirmPopUp(cancleOrContinue) {
      // The list has to be emptied on dismiss, not just hidden: the lookups append to it, so a
      // second lookup would otherwise re-show every warning the clerk has already cleared.
      // (The load path clears it before each load, which is why this never showed before.)
      this.warningMessage = [];
      this.showOrHideWarningMessage = false;
      // rows a lookup resolved but has not committed yet - taken either way, so a later popup
      // cannot commit them a second time
      const heldRows = this.pendingTaxRows;
      this.pendingTaxRows = [];
      if (cancleOrContinue === 'Cancel') {
        // Cancelling a lookup's warning means "do not take this record" - drop the held rows and
        // leave the payment as it was. Only a warning with nothing held is the load-time kind,
        // where Cancel has always closed the whole dialog.
        if (!heldRows.length) {
          this.setIsVisible([this.parentCrudKey, false]);
          return;
        }
        // the dialog stays open, so the header takes the cursor back for the next record
        this.$emit('warningsAnswered');
        return;
      }
      this.appendTaxRows(heldRows);
      this.$emit('warningsAnswered');
    },
    handleCircleClass(textName) {
      if (textName === 'extraPayment' || textName === 'alreadyPayExits') return 'ChangeIconRedColor';
      if (textName === 'defaultText') return 'removeIcon';
      return 'ChangeIconYellowColor';
    },
  },
  created() {
    // Initialize pagination settings on component creation
    const paginationItem = {
      itemsperpage: this.getSettings.itemsperpage || 100,
      serverItemsLength: this.gridServerItemsLength(),
      footerProps: this.gridFooterProps,
    };
    this.setPaginationItems([this.baseCrudKey, paginationItem]);
    this.loadReasonCode({
      criteria: { sortBy: [{ column: 'reasonCode', direction: 'asc' }] },
      postProcessTrans: taxReasonListDropDownPostProcessTrans,
      baseCrudKey: 'reasonListDropDown',
    });
  },
  mounted() {
    // A payment being entered: the dialog is already open the first time this mounts, so the
    // visibility watcher never sees that transition and this is where the grid takes the payment
    // on - including pricing whatever rows a tax inquiry seeded onto it.
    if (this.isNewPayment) {
      this.resetForNewPayment();
      return;
    }
    // For picklist mode (no paymentNumber), initialize serverTotals
    // from existing editedItem.taxDetails
    // This ensures footer totals display correctly when dialog opens
    if (!this.paymentNumber && this.editedItem && this.editedItem.taxDetails) {
      const taxDetails = this.editedItem.taxDetails || [];
      const taxItems = taxDetails.filter(
        (item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND',
      );

      if (taxItems.length > 0) {
        this.serverTotals = {
          totalTaxAmt: taxItems.reduce(
            (sum, item) => sum + amountToNumber(item.taxAmt), 0,
          ),
          totalDue: taxItems.reduce(
            (sum, item) => sum + amountToNumber(item.total), 0,
          ),
          totalPenalty: taxItems.reduce(
            (sum, item) => sum + amountToNumber(item.penaltyAmt), 0,
          ),
          totalFees: taxItems.reduce(
            (sum, item) => sum + amountToNumber(item.totalFees), 0,
          ),
          totalRecords: taxDetails.length,
        };

        // Update pagination with the record count
        const paginationItem = {
          itemsperpage: this.getSettings.itemsperpage || 100,
          serverItemsLength: this.gridServerItemsLength(),
          footerProps: this.gridFooterProps,
        };
        this.setPaginationItems([this.baseCrudKey, paginationItem]);

        // Also set the items in the store for CRUDList to display
        this.setItems([this.baseCrudKey, taxDetails]);
      }
    }
  },
  beforeDestroy() {
    // cleanup store data on close / destroy, instead of when loading
    this.resetStoreData();
  },
  watch: {
    'paymentSettingItem.showCertNumberOnPaymentScreen': {
      handler(val) {
        this.headers = this.buildHeaders(val);
      },
      immediate: true,
    },
    // The Type cell's options are baked into the header, and the enum they come from arrives
    // after the first render (cacheEnums is not awaited) - rebuild the headers when it does, or
    // the column stays a dropdown with nothing in it.
    taxPaymentTypeOptions: {
      handler() {
        this.headers = this.buildHeaders();
      },
    },
    /* Nothing writes newTaxDetailsToAdd now that the "Add Tax Details to Payment" card is
       commented out, so this merge-to-main-list watcher can never fire.
    // Watch for changes in newTaxDetailsToAdd and merge them to main list.
    //
    // This watcher and addToList() both commit staged rows, and they used to apply DIFFERENT
    // duplicate rules - this one checked both stores, addToList checked neither - so a row the
    // watcher had just committed could be appended a second time by an addToList fired in the
    // same tick (the Payment Info field activity and unpaids-loaded triggers both do). Both now
    // hand their rows to commitTaxRows, which owns the one rule.
    newTaxDetailsToAdd: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) {
          return;
        }

        const currentItems = this.items(this.baseCrudKey) || [];

        // Only rows that resolved against tax inquiry: itsNewRecord is explicitly set to false
        // once the fetch succeeded, so anything still being typed is left staged.
        const completeNewItems = newVal.filter((newItem) => newItem.taxYear
          && newItem.taxId
          && newItem.ownerName
          && newItem.itsNewRecord === false);

        if (completeNewItems.length > 0) {
          this.commitTaxRows([...currentItems, ...completeNewItems], {
            addedItems: completeNewItems,
          });

          this.$nextTick(() => {
            this.$forceUpdate();
          });

          // Clear the handled rows out of the staging grid - added or rejected as duplicates
          // either way, they have had their turn.
          this.newTaxDetailsToAdd = newVal.filter((item) => {
            const wasHandled = completeNewItems.some((handledItem) => (
              String(handledItem.taxYear) === String(item.taxYear)
              && String(handledItem.taxId) === String(item.taxId)
            ));
            return !wasHandled;
          });
        }
      },
      deep: true,
    },
    */
    // A payment being entered starts on an empty grid. Not immediate: on the first mount the
    // dialog is already open and the grid is fresh anyway - it is the re-opens, which reuse this
    // component, that have the previous payment's rows still in the store.
    parentDialogIsOpen: {
      handler(isOpen) {
        if (!isOpen) return;
        // A fresh dialog session gets a fresh pick list. beforeDestroy normally clears this
        // (resetStoreData), but the payment dialog is not always torn down between payments -
        // Vuetify keeps a dialog's content mounted once it has been opened - and a stale
        // pickListLoaded would leave the next payment looking at the last one's rows.
        this.pickListLoaded = false;
        if (this.isNewPayment) this.resetForNewPayment();
      },
    },
    /**
     * What is still due changed - with 'Load Unpaids Automatically' on, take it. This is the one
     * trigger for the setting: every path that can leave a record due (the scan finishing, a
     * keyed lookup, a parcel lookup, a deleted row) ends in this set changing, and taking the
     * records empties it again. autoLoadUnpaidRows is a no-op with the setting off.
     */
    unpaidTaxRecords: {
      handler() {
        this.autoLoadUnpaidRows();
      },
      immediate: true,
    },
    fiscalYear: {
      handler(newVal) {
        if (newVal && this.paymentNumber) {
          this.loadLargePaymentData();
        }
      },
      immediate: true,
    },
    paymentNumber: {
      handler(newVal) {
        if (newVal && this.fiscalYear) {
          this.loadLargePaymentData();
        }
      },
      immediate: true,
    },
    // Watch for changes in calculated totals and emit to parent
    'calculatedTotals.total': {
      handler(newTotal) {
        // Emit the new total to parent component
        this.$emit('totalChanged', newTotal);
      },
    },
    // Watch for serverTotals changes and emit to parent
    serverTotals: {
      handler(newVal) {
        // Emptied grids are emitted too. Gating this on totalRecords > 0 meant deleting the
        // last row left the dialog's Total Due showing the total of the row that is gone.
        if (newVal) {
          // Emit server totals to parent for accurate display across all pages
          this.$emit('serverTotalsChanged', {
            totalDue: newVal.totalDue,
            totalTaxAmt: newVal.totalTaxAmt,
            totalPenalty: newVal.totalPenalty,
            totalFees: newVal.totalFees,
            totalRecords: newVal.totalRecords,
          });
        }
      },
      deep: true,
      immediate: true,
    },
    // Watch for editedItem.taxDetails changes in picklist mode
    // This handles the case when taxDetails is populated after component mount
    'editedItem.taxDetails': {
      handler(newVal) {
        // Only for picklist mode (no paymentNumber) and when serverTotals not set
        const isPicklistMode = !this.paymentNumber;
        const hasData = newVal && newVal.length > 0;
        const totalsNotSet = this.serverTotals.totalRecords === 0;

        if (isPicklistMode && hasData && totalsNotSet) {
          const taxItems = newVal.filter(
            (item) => !item.taxType || item.taxType === 'TAXPAYMENT' || item.taxType === 'TAXREFUND',
          );

          if (taxItems.length > 0) {
            this.serverTotals = {
              totalTaxAmt: taxItems.reduce(
                (sum, item) => sum + amountToNumber(item.taxAmt), 0,
              ),
              totalDue: taxItems.reduce(
                (sum, item) => sum + amountToNumber(item.total), 0,
              ),
              totalPenalty: taxItems.reduce(
                (sum, item) => sum + amountToNumber(item.penaltyAmt), 0,
              ),
              totalFees: taxItems.reduce(
                (sum, item) => sum + amountToNumber(item.totalFees), 0,
              ),
              totalRecords: newVal.length,
            };

            // Update pagination with the record count
            const paginationItem = {
              itemsperpage: this.getSettings.itemsperpage || 100,
              serverItemsLength: this.gridServerItemsLength(),
              footerProps: this.gridFooterProps,
            };
            this.setPaginationItems([this.baseCrudKey, paginationItem]);

            // Also set the items in the store for CRUDList to display
            this.setItems([this.baseCrudKey, newVal]);
          }
        }
      },
      immediate: true,
      deep: true,
    },
    // Update totals when props change
    totalDue: {
      handler() {
        // Force reactivity update for UI components that depend on totalDue
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
    },
    totalPaid: {
      handler() {
        // Force reactivity update for UI components that depend on totalPaid
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
    },
    cashChange: {
      handler() {
        // Force reactivity update for UI components that depend on cashChange
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      },
    },
  },
};

</script>
<style scoped>
/* Same two footer labels dialogTaxDetails styles under its grid - scoped there, so they come
   along with the row rather than being shared. The border each of them used to carry is on
   the row now (large-payment-footer-row): drawn per label it broke wherever a column was
   empty, and the labels no longer sit in fixed columns for it to line up with. */
.large-payment-footer-row {
  border-top: 1px solid rgb(217, 214, 214);
}
.blue-text {
  text-align: end;
  color: #2196f3 !important;
  padding: 0.5rem;
}
/* Both of these are one reading, so they keep to one line - each wrapped in the middle
   ("Other / Taxes Due") when it was boxed into a fixed share of the row. */
.large-payment-running-total,
.large-payment-other-taxes-due {
  white-space: nowrap;
}
.blue-text-left {
  text-align: start;
  color: #2196f3 !important;
  padding: 0.5rem;
}
</style>
